/**
 * Weapons for modular figures. Most are held in the right hand: the grip sits in the palm (through
 * the gap a gripping hand leaves) and the weapon points the way the pose swings it — the blade
 * angles the classic figure's poses were drawn with, so a cut, a thrust or a wind-up reads the
 * same as in the game today. Paired weapons fill the left hand too, aimed by the left arm's own
 * pose; a bow is held in the left hand; fist weapons are wraps on both hands, gripping nothing.
 * Where the classic figure swung the blade behind the body (a wind-up, a low back guard) it is
 * drawn behind it here too.
 */
import { shade } from '../palette';
import { LIMB, PAD_TOP, type Rig, SHEATHE_FRAMES, stowPhase, type View } from '../sprites';
import { flutter, type Layer, type Piece, px, segment, zOf } from './figure';
import { SEEN_DEPTH, SEEN_OUT, type Side, type Skeleton } from './skeleton';

type G = CanvasRenderingContext2D;
type Pt = { x: number; y: number };

export type WeaponKind =
  | 'jian'
  | 'dao'
  | 'spear'
  | 'qiang'
  | 'bow'
  | 'fist'
  | 'umbrella'
  | 'fan'
  | 'kris'
  | 'staff'
  | 'spike'
  | 'chain'
  | 'glaive'
  | 'halberd'
  | 'axe'
  | 'hammer'
  | 'whisk'
  | 'hook'
  | 'whip'
  | 'flute'
  | 'brush';

export interface WeaponDesign {
  id: string;
  label: string;
  kind: WeaponKind;
  /** Which hands hold it: the right, both (a pair), the left (a bow), or none (wraps on the fists). */
  hands: 'right' | 'pair' | 'left' | 'fists';
  /** Reach ahead of the hand, and how far the shaft or pommel runs back behind it. */
  ahead: number;
  behind: number;
  /**
   * Colours: the blade (or head, canopy, paper, hair) in its light and dark, the guard and fittings,
   * the grip or shaft, and the tassel.
   */
  colours: { blade: string; edge: string; guard: string; grip: string; tassel: string };
  /** The shape, beyond its kind's (see `WeaponForm`). */
  form?: WeaponForm;
}

/** How one design of a kind differs from the others: the blade's cut, its fittings and its head. */
export interface WeaponForm {
  /** Half-width of a blade at the guard, and its width at the point end as a fraction of that. */
  width?: number;
  taper?: number;
  /** How long the point is, as a fraction of the blade. */
  point?: number;
  /** A sabre's bow: how far the blade curves over its length. */
  curve?: number;
  /** Extra width swelling toward the tip (an ox-tail or horse-cutting sabre). */
  belly?: number;
  /** The back cut away toward the point (a ghost-head sabre). */
  clip?: boolean;
  /** A raised ridge down the middle of the blade. */
  ridge?: boolean;
  /** Teeth along the back of the blade. */
  teeth?: boolean;
  /** Iron rings threaded along the back (a nine-ring sabre) or on a monk's staff head. */
  rings?: number;
  /** A line inlaid down the blade (gold, jade, a glowing rune), and seven-star dots. */
  inlay?: string;
  stars?: number;
  /** Waves in a wavy blade (a kris, a snake spear). */
  waves?: number;
  guard?: 'bar' | 'cloud' | 'disc' | 'wings' | 'beast' | 'crescent' | 'none';
  /** A stone set in the guard at double resolution (false for plain wood and practice blades). */
  gem?: boolean;
  pommel?: 'knob' | 'ring' | 'cap' | 'none';
  tassel?: 'short' | 'long' | 'none';
  /** The head of a polearm, axe, hammer, staff, chain, fist weapon or whip (see each kind). */
  head?: string;
  /** Size of that head, 1 by default. */
  size?: number;
  /** Metal bands round a shaft. */
  bands?: number;
  /** Sparks of light round the blade of a famed weapon. */
  glow?: string;
}

const STEEL = { blade: '#e6ecf4', edge: '#7f93b0', guard: '#e8c27e', grip: '#3a2f4a', tassel: '#c63c4a' };
const WOOD = { blade: '#a8774a', edge: '#5e3c22', guard: '#e8c27e', grip: '#6e4a2c', tassel: '#c63c4a' };
const SPEAR = { ...WOOD, blade: '#e6ecf4', edge: '#7f93b0' };
const BLACK = { blade: '#5a5e6a', edge: '#202430', guard: '#9aa2b0', grip: '#1a1418', tassel: '#2a2a34' };
const BRONZE = { blade: '#d8a868', edge: '#7a4e24', guard: '#e8c27e', grip: '#4a2a1a', tassel: '#c63c4a' };
const GOLD = { blade: '#f4d890', edge: '#a8782a', guard: '#f0c040', grip: '#5a1a14', tassel: '#d8303a' };
const JADE = { blade: '#a8e8c8', edge: '#2f7a5a', guard: '#e8c27e', grip: '#2a3a30', tassel: '#2f8a5a' };
const AZURE = { blade: '#b8dcff', edge: '#2e5aa8', guard: '#d8e4f4', grip: '#1c2a4a', tassel: '#3a7ae0' };
const CRIMSON = { blade: '#f0a0a0', edge: '#8a1420', guard: '#e8b84a', grip: '#2a0a0e', tassel: '#c8202a' };
const FROST = { blade: '#eef8ff', edge: '#7ab0d8', guard: '#c8e4f8', grip: '#2a3a5a', tassel: '#9ad0f8' };
const VIOLET = { blade: '#c8b0f0', edge: '#3a1a6a', guard: '#e8c27e', grip: '#1a0e2a', tassel: '#8a3ad8' };
const BONE = { blade: '#ece2c8', edge: '#9a8a68', guard: '#c8b48a', grip: '#4a3a2a', tassel: '#8a2a1a' };
const BAMBOO = { blade: '#c8c070', edge: '#6a7a2a', guard: '#8a9a3a', grip: '#6a7a2a', tassel: '#2f8a5a' };
const SILVER = { blade: '#f4f6fa', edge: '#8a94a8', guard: '#e4e8f0', grip: '#e8ecf4', tassel: '#e8ecf4' };
const EBONY = { blade: '#4a3a34', edge: '#1a1210', guard: '#e8c27e', grip: '#1a1210', tassel: '#c63c4a' };
const RED_LACQUER = {
  blade: '#b8302a',
  edge: '#5a1010',
  guard: '#e8c27e',
  grip: '#5a1010',
  tassel: '#e8c27e',
};

/** A shaft weapon's colours: its head from `head`, its shaft from `shaft`. */
const pole = (head: typeof STEEL, shaft: typeof WOOD, tassel = '#c63c4a') => ({
  ...head,
  grip: shaft.blade,
  tassel,
});

type W = Omit<WeaponDesign, 'kind' | 'hands'> & { hands?: WeaponDesign['hands'] };
/** Designs of one kind, held as the kind is unless a design says otherwise. */
const family = (kind: WeaponKind, hands: WeaponDesign['hands'], list: W[]): WeaponDesign[] =>
  list.map((w) => ({ ...w, kind, hands: w.hands ?? hands }));

export const WEAPONS: readonly WeaponDesign[] = [
  // ── กระบี่ (jian): straight, double-edged ──
  ...family('jian', 'right', [
    // The starter: a practice sword carved from one piece of wood.
    {
      id: 'woodSword',
      label: 'กระบี่ไม้',
      ahead: 22,
      behind: 4,
      colours: { ...WOOD, guard: '#8a5a32', grip: '#5e3c22' },
      form: { width: 1, taper: 0.8, point: 0.12, guard: 'bar', gem: false, pommel: 'cap', tassel: 'none' },
    },
    { id: 'shortSword', label: 'ดาบสั้น', ahead: 14, behind: 3, colours: STEEL, form: { ridge: true } },
    { id: 'longSword', label: 'ดาบยาว', ahead: 26, behind: 5, colours: STEEL, form: { ridge: true } },
    {
      id: 'azureSword',
      label: 'กระบี่ครามวารี',
      ahead: 25,
      behind: 5,
      colours: AZURE,
      form: { ridge: true, guard: 'cloud', tassel: 'long', inlay: '#e8f4ff' },
    },
    {
      id: 'crimsonSword',
      label: 'กระบี่โลหิตชาด',
      ahead: 25,
      behind: 5,
      colours: CRIMSON,
      form: { guard: 'beast', pommel: 'ring', inlay: '#ff4a4a', glow: '#ff6a5a' },
    },
    {
      id: 'jadeSword',
      label: 'กระบี่หยกมรกต',
      ahead: 24,
      behind: 5,
      colours: JADE,
      form: { width: 1.2, ridge: true, guard: 'disc', tassel: 'long' },
    },
    {
      id: 'frostSword',
      label: 'กระบี่ธารน้ำแข็ง',
      ahead: 27,
      behind: 5,
      colours: FROST,
      form: { taper: 0.5, point: 0.25, guard: 'wings', glow: '#cfeaff' },
    },
    {
      id: 'dragonSword',
      label: 'กระบี่มังกรทอง',
      ahead: 26,
      behind: 6,
      colours: { ...STEEL, guard: '#f0c040', tassel: '#d8303a' },
      form: { width: 1.3, ridge: true, guard: 'beast', pommel: 'ring', tassel: 'long', inlay: '#f0c040' },
    },
    {
      id: 'inkSword',
      label: 'กระบี่หมึกราตรี',
      ahead: 25,
      behind: 5,
      colours: { ...BLACK, guard: '#c8ccd8', tassel: '#e8ecf4' },
      form: { ridge: true, guard: 'disc', pommel: 'knob', tassel: 'long' },
    },
    {
      id: 'sevenStarSword',
      label: 'กระบี่เจ็ดดาว',
      ahead: 26,
      behind: 5,
      colours: { ...STEEL, guard: '#e8c27e', tassel: '#3a7ae0' },
      form: { width: 1.2, stars: 7, guard: 'cloud', tassel: 'short' },
    },
    {
      id: 'heavenSword',
      label: 'กระบี่บันไดสวรรค์',
      ahead: 26,
      behind: 5,
      colours: SILVER,
      form: { ridge: true, guard: 'wings', pommel: 'ring', tassel: 'long', glow: '#fff4c8' },
    },
    {
      id: 'demonSword',
      label: 'กระบี่มารพันกระบี่',
      ahead: 27,
      behind: 6,
      colours: { blade: '#4a4258', edge: '#140e1c', guard: '#e8b84a', grip: '#1a0a0e', tassel: '#8a2ad8' },
      form: { width: 1.3, guard: 'beast', pommel: 'ring', inlay: '#e8303a', glow: '#b06aff', tassel: 'long' },
    },
    // A plain steel blade on a jade guard and pommel, a pale green silk tassel.
    {
      id: 'jadeHiltSword',
      label: 'กระบี่ด้ามหยก',
      ahead: 25,
      behind: 5,
      colours: { ...STEEL, guard: '#8cc4a2', grip: '#2a3a30', tassel: '#b4d8b8' },
      form: { ridge: true, guard: 'cloud', pommel: 'knob', tassel: 'long' },
    },
    {
      id: 'twinSword',
      label: 'ดาบคู่',
      hands: 'pair',
      ahead: 16,
      behind: 3,
      colours: STEEL,
      form: { ridge: true },
    },
  ]),
  // ── ดาบ (dao): single-edged, the back bowing toward the point ──
  ...family('dao', 'right', [
    { id: 'dao', label: 'กระบี่', ahead: 19, behind: 4, colours: STEEL, form: { curve: 1.5 } },
    {
      id: 'twinDao',
      label: 'กระบี่คู่',
      hands: 'pair',
      ahead: 15,
      behind: 3,
      colours: STEEL,
      form: { curve: 1.5 },
    },
    {
      id: 'greatDao',
      label: 'ดาบใหญ่ฟันม้า',
      ahead: 27,
      behind: 8,
      colours: STEEL,
      form: { width: 1.8, belly: 1, curve: 1, guard: 'disc', pommel: 'ring' },
    },
    {
      id: 'willowDao',
      label: 'ดาบใบหลิว',
      ahead: 20,
      behind: 4,
      colours: STEEL,
      form: { width: 1.3, curve: 2.5, guard: 'disc', tassel: 'short' },
    },
    {
      id: 'oxtailDao',
      label: 'ดาบหางวัว',
      ahead: 20,
      behind: 4,
      colours: { ...STEEL, guard: '#c8a050' },
      form: { width: 1.6, belly: 1.6, curve: 1.5, clip: true, guard: 'bar', tassel: 'long' },
    },
    {
      id: 'ghostDao',
      label: 'ดาบหัวผี',
      ahead: 20,
      behind: 5,
      colours: { ...BLACK, tassel: '#8a1420' },
      form: { width: 1.8, belly: 1.2, clip: true, curve: 1, guard: 'beast', pommel: 'ring', tassel: 'long' },
    },
    {
      id: 'nineRingDao',
      label: 'ดาบเก้าห่วง',
      ahead: 23,
      behind: 5,
      colours: { ...STEEL, guard: '#e8c27e' },
      form: { width: 2, belly: 0.6, curve: 1, rings: 9, guard: 'disc', pommel: 'ring' },
    },
    {
      id: 'hellSabre',
      label: 'ดาบบันไดนรก',
      ahead: 22,
      behind: 5,
      colours: CRIMSON,
      form: { width: 1.6, curve: 2, teeth: true, guard: 'beast', inlay: '#ff4a3a', glow: '#ff5a3a' },
    },
    {
      id: 'moonDao',
      label: 'ดาบจันทร์เพ็ญ',
      ahead: 22,
      behind: 4,
      colours: SILVER,
      form: { width: 1.4, curve: 3.5, guard: 'crescent', tassel: 'long', glow: '#e8f0ff' },
    },
    {
      id: 'tigerDao',
      label: 'ดาบพยัคฆ์สัมฤทธิ์',
      ahead: 21,
      behind: 5,
      colours: BRONZE,
      form: { width: 1.7, belly: 0.8, curve: 1.5, guard: 'beast', pommel: 'knob', tassel: 'short' },
    },
    {
      id: 'wolfFangDao',
      label: 'ดาบเขี้ยวหมาป่า',
      ahead: 20,
      behind: 4,
      colours: { ...BLACK, guard: '#7a7e8a' },
      form: { width: 1.5, curve: 1.5, teeth: true, guard: 'crescent', pommel: 'cap' },
    },
    {
      id: 'goldDao',
      label: 'ดาบทองพิทักษ์',
      ahead: 22,
      behind: 5,
      colours: GOLD,
      form: {
        width: 1.6,
        belly: 0.8,
        curve: 1.5,
        guard: 'wings',
        pommel: 'ring',
        inlay: '#fff4c8',
        tassel: 'long',
      },
    },
    // A broad, heavy blade with a thick back, a ring pommel on a leather-wrapped grip.
    {
      id: 'heavyBackDao',
      label: 'ดาบใหญ่หลังหนา',
      ahead: 24,
      behind: 7,
      colours: { blade: '#dfe4ea', edge: '#6e7a8a', guard: '#b08a50', grip: '#4a3424', tassel: '#9a3a2e' },
      form: { width: 2.2, curve: 0.8, point: 0.22, guard: 'bar', pommel: 'ring', tassel: 'short' },
    },
  ]),
  // ── หอก (spear): a broad head on a long shaft ──
  ...family('spear', 'right', [
    { id: 'spear', label: 'หอก', ahead: 20, behind: 12, colours: SPEAR, form: { head: 'leaf' } },
    {
      id: 'bambooSpear',
      label: 'หอกไผ่',
      ahead: 20,
      behind: 12,
      colours: pole(STEEL, BAMBOO),
      form: { head: 'leaf', size: 0.8, bands: 3 },
    },
    {
      id: 'boarSpear',
      label: 'หอกล่าหมูป่า',
      ahead: 20,
      behind: 12,
      colours: SPEAR,
      form: { head: 'boar', size: 1.1 },
    },
    {
      id: 'snakeSpear',
      label: 'หอกงูเลื้อย',
      ahead: 22,
      behind: 13,
      colours: pole(STEEL, EBONY),
      form: { head: 'snake', waves: 3, size: 1.2 },
    },
    {
      id: 'trident',
      label: 'ส้อมสามง่าม',
      ahead: 21,
      behind: 12,
      colours: pole(STEEL, EBONY),
      form: { head: 'trident' },
    },
    {
      id: 'bronzeSpear',
      label: 'หอกสัมฤทธิ์',
      ahead: 20,
      behind: 12,
      colours: pole(BRONZE, WOOD),
      form: { head: 'leaf', size: 1.2, bands: 2 },
    },
    {
      id: 'blackSpear',
      label: 'หอกเหล็กดำ',
      ahead: 21,
      behind: 13,
      colours: pole(BLACK, BLACK),
      form: { head: 'leaf', size: 1.3, bands: 4 },
    },
    {
      id: 'jadeSpear',
      label: 'หอกหยกเขียว',
      ahead: 21,
      behind: 12,
      colours: pole(JADE, RED_LACQUER),
      form: { head: 'leaf', ridge: true, bands: 2 },
    },
    {
      id: 'flameSpear',
      label: 'หอกเปลวอัคคี',
      ahead: 22,
      behind: 12,
      colours: pole(CRIMSON, EBONY),
      form: { head: 'snake', waves: 2, size: 1.3, glow: '#ff7a3a' },
    },
    {
      id: 'dragonSpear',
      label: 'หอกมังกรเหิน',
      ahead: 23,
      behind: 13,
      colours: pole(GOLD, RED_LACQUER),
      form: { head: 'trident', size: 1.2, bands: 3, glow: '#ffe08a' },
    },
  ]),
  // ── ทวน (qiang): a slim head with a horsehair tassel ──
  ...family('qiang', 'right', [
    { id: 'qiang', label: 'ทวน', ahead: 22, behind: 14, colours: SPEAR, form: { tassel: 'long' } },
    {
      id: 'silverQiang',
      label: 'ทวนเงินพิทักษ์ใจ',
      ahead: 23,
      behind: 14,
      colours: pole(SILVER, SILVER, '#e8ecf4'),
      form: { tassel: 'long', bands: 2 },
    },
    {
      id: 'plumQiang',
      label: 'ทวนดอกเหมย',
      ahead: 22,
      behind: 14,
      colours: pole(STEEL, RED_LACQUER, '#f08ab0'),
      form: { tassel: 'long' },
    },
    {
      id: 'blackQiang',
      label: 'ทวนเหล็กดำ',
      ahead: 22,
      behind: 14,
      colours: pole(BLACK, BLACK, '#1a1a20'),
      form: { tassel: 'long', size: 1.2 },
    },
    {
      id: 'goldQiang',
      label: 'ทวนทองคำ',
      ahead: 23,
      behind: 14,
      colours: pole(GOLD, RED_LACQUER, '#d8303a'),
      form: { tassel: 'long', bands: 3 },
    },
    {
      id: 'whiteQiang',
      label: 'ทวนพู่ขาว',
      ahead: 22,
      behind: 14,
      colours: pole(STEEL, WOOD, '#f4f0e8'),
      form: { tassel: 'long' },
    },
    {
      id: 'thunderQiang',
      label: 'ทวนสายฟ้า',
      ahead: 23,
      behind: 14,
      colours: pole(AZURE, BLACK, '#3a7ae0'),
      form: { tassel: 'long', glow: '#9ad0ff' },
    },
    {
      id: 'jadeQiang',
      label: 'ทวนหยกมังกร',
      ahead: 22,
      behind: 14,
      colours: pole(JADE, EBONY, '#2f8a5a'),
      form: { tassel: 'long', bands: 2 },
    },
    {
      id: 'shortQiang',
      label: 'ทวนสั้นทหารม้า',
      ahead: 18,
      behind: 10,
      colours: SPEAR,
      form: { tassel: 'short', size: 1.1 },
    },
    {
      id: 'bloodQiang',
      label: 'ทวนโลหิตทะลวง',
      ahead: 24,
      behind: 14,
      colours: pole(CRIMSON, EBONY, '#8a1420'),
      form: { tassel: 'long', size: 1.3, glow: '#ff5a3a' },
    },
  ]),
  // ── ธนู (bow) ──
  ...family('bow', 'left', [
    { id: 'bow', label: 'ธนู', ahead: 0, behind: 0, colours: { ...WOOD, blade: '#8a5a32', edge: '#f0ece2' } },
    {
      id: 'shortBow',
      label: 'ธนูสั้นพราน',
      ahead: 0,
      behind: 0,
      colours: { ...WOOD, blade: '#9a6a3a', edge: '#e8e0d0' },
      form: { size: 0.8 },
    },
    {
      id: 'longBow',
      label: 'ธนูยาว',
      ahead: 0,
      behind: 0,
      colours: { ...WOOD, blade: '#7a4a2a', edge: '#f0ece2' },
      form: { size: 1.25 },
    },
    {
      id: 'hornBow',
      label: 'ธนูเขาสัตว์',
      ahead: 0,
      behind: 0,
      colours: { ...BONE, blade: '#3a2a22', edge: '#e8e0d0', guard: '#ece2c8' },
      form: { head: 'recurve' },
    },
    { id: 'bambooBow', label: 'ธนูไผ่', ahead: 0, behind: 0, colours: { ...BAMBOO, edge: '#f0ece2' } },
    {
      id: 'blackBow',
      label: 'ธนูเหล็กดำ',
      ahead: 0,
      behind: 0,
      colours: { ...BLACK, blade: '#3a3e48', edge: '#d8dce4' },
      form: { head: 'recurve', size: 1.1 },
    },
    {
      id: 'jadeBow',
      label: 'ธนูหยก',
      ahead: 0,
      behind: 0,
      colours: { ...JADE, edge: '#f0ece2' },
      form: { head: 'recurve' },
    },
    {
      id: 'crimsonBow',
      label: 'ธนูชาดล่าเหยี่ยว',
      ahead: 0,
      behind: 0,
      colours: { ...RED_LACQUER, edge: '#f0ece2' },
      form: { head: 'recurve', size: 1.1 },
    },
    {
      id: 'moonBow',
      label: 'ธนูจันทร์เสี้ยว',
      ahead: 0,
      behind: 0,
      colours: { ...SILVER, edge: '#c8e4f8' },
      form: { size: 1.2, glow: '#e8f0ff' },
    },
    {
      id: 'phoenixBow',
      label: 'ธนูหงส์เพลิง',
      ahead: 0,
      behind: 0,
      colours: { ...GOLD, edge: '#ff8a3a' },
      form: { head: 'recurve', size: 1.25, glow: '#ff9a4a' },
    },
  ]),
  // ── หมัด (fist weapons): wraps, knuckles, claws ──
  ...family('fist', 'fists', [
    {
      id: 'fist',
      label: 'หมัด',
      ahead: 0,
      behind: 0,
      colours: { ...STEEL, blade: '#f0ece2', edge: '#c9bfa8' },
    },
    {
      id: 'leatherWraps',
      label: 'ผ้าพันหนัง',
      ahead: 0,
      behind: 0,
      colours: { ...WOOD, blade: '#8a5a32', edge: '#5e3c22' },
    },
    {
      id: 'redWraps',
      label: 'ผ้าพันแดงนักมวย',
      ahead: 0,
      behind: 0,
      colours: { ...STEEL, blade: '#c8303a', edge: '#7a1820' },
    },
    {
      id: 'ironKnuckles',
      label: 'สนับมือเหล็ก',
      ahead: 0,
      behind: 0,
      colours: { ...BLACK, blade: '#e8e0d0', edge: '#a89a80' },
      form: { head: 'knuckle' },
    },
    {
      id: 'bronzeKnuckles',
      label: 'สนับมือสัมฤทธิ์',
      ahead: 0,
      behind: 0,
      colours: { ...BRONZE, blade: '#5e3c22', edge: '#3a2416', guard: '#d8a868' },
      form: { head: 'knuckle' },
    },
    {
      id: 'tigerClaws',
      label: 'กรงเล็บพยัคฆ์',
      ahead: 0,
      behind: 0,
      colours: { ...STEEL, blade: '#4a3a2a', edge: '#2a2018' },
      form: { head: 'claw' },
    },
    {
      id: 'eagleClaws',
      label: 'กรงเล็บอินทรี',
      ahead: 0,
      behind: 0,
      colours: { ...GOLD, blade: '#3a2a1a', edge: '#1a120c', guard: '#f0c040' },
      form: { head: 'claw', size: 1.3 },
    },
    {
      id: 'ironGauntlets',
      label: 'ถุงมือเกราะ',
      ahead: 0,
      behind: 0,
      colours: { ...BLACK, blade: '#7a808c', edge: '#3a3e48' },
      form: { head: 'gauntlet' },
    },
    {
      id: 'goldGauntlets',
      label: 'ถุงมือทองวัชระ',
      ahead: 0,
      behind: 0,
      colours: { ...GOLD, blade: '#e8c27e', edge: '#a8782a' },
      form: { head: 'gauntlet', glow: '#ffe08a' },
    },
    {
      id: 'demonClaws',
      label: 'กรงเล็บมาร',
      ahead: 0,
      behind: 0,
      colours: { ...VIOLET, blade: '#2a1a3a', edge: '#140a20' },
      form: { head: 'claw', size: 1.4, glow: '#b06aff' },
    },
  ]),
  // ── ร่ม (umbrella) ──
  ...family('umbrella', 'right', [
    {
      id: 'umbrella',
      label: 'ร่ม',
      ahead: 18,
      behind: 4,
      colours: { ...WOOD, blade: '#c63c4a', edge: '#8e2433' },
    },
    {
      id: 'oilUmbrella',
      label: 'ร่มกระดาษน้ำมัน',
      ahead: 18,
      behind: 4,
      colours: { ...WOOD, blade: '#e8b860', edge: '#a8782a' },
    },
    {
      id: 'inkUmbrella',
      label: 'ร่มหมึกจันทร์',
      ahead: 18,
      behind: 4,
      colours: { ...EBONY, blade: '#2a2a34', edge: '#14141c', tassel: '#e8ecf4' },
    },
    {
      id: 'lotusUmbrella',
      label: 'ร่มบัวขาว',
      ahead: 18,
      behind: 4,
      colours: { ...WOOD, blade: '#f4f0e8', edge: '#e8a0b8' },
    },
    {
      id: 'blueUmbrella',
      label: 'ร่มฟ้าคราม',
      ahead: 18,
      behind: 4,
      colours: { ...WOOD, blade: '#4a7ac8', edge: '#2a4a8a', tassel: '#f4f0e8' },
    },
    {
      id: 'jadeUmbrella',
      label: 'ร่มหยกฝน',
      ahead: 18,
      behind: 4,
      colours: { ...EBONY, blade: '#7ac8a0', edge: '#2f7a5a', tassel: '#2f8a5a' },
    },
    {
      id: 'peachUmbrella',
      label: 'ร่มดอกท้อ',
      ahead: 18,
      behind: 4,
      colours: { ...WOOD, blade: '#f0a8c0', edge: '#c86a8a' },
    },
    {
      id: 'ironUmbrella',
      label: 'ร่มเหล็กพรางดาบ',
      ahead: 19,
      behind: 4,
      colours: { ...BLACK, blade: '#5a5e6a', edge: '#202430' },
      form: { head: 'blade' },
    },
    {
      id: 'goldUmbrella',
      label: 'ร่มทองราชครู',
      ahead: 18,
      behind: 4,
      colours: { ...GOLD, blade: '#e8c27e', edge: '#a8782a' },
    },
    {
      id: 'bloodUmbrella',
      label: 'ร่มโลหิตพันศพ',
      ahead: 19,
      behind: 4,
      colours: { ...EBONY, blade: '#8a1420', edge: '#4a0a10' },
      form: { head: 'blade', glow: '#ff5a3a' },
    },
  ]),
  // ── พัด (fan) ──
  ...family('fan', 'right', [
    { id: 'fan', label: 'พัด', ahead: 8, behind: 1, colours: { ...WOOD, blade: '#f0ece2', edge: '#c9bfa8' } },
    {
      id: 'inkFan',
      label: 'พัดภาพหมึก',
      ahead: 8,
      behind: 1,
      colours: { ...EBONY, blade: '#f0ece2', edge: '#3a3438' },
    },
    {
      id: 'peachFan',
      label: 'พัดดอกท้อ',
      ahead: 8,
      behind: 1,
      colours: { ...WOOD, blade: '#f8d8e0', edge: '#d8708a' },
    },
    {
      id: 'goldFan',
      label: 'พัดทองเปลว',
      ahead: 8,
      behind: 1,
      colours: { ...GOLD, blade: '#f4d890', edge: '#a8782a' },
    },
    {
      id: 'jadeFan',
      label: 'พัดซี่หยก',
      ahead: 8,
      behind: 1,
      colours: { ...JADE, blade: '#e8f4ec', edge: '#2f7a5a' },
    },
    {
      id: 'ironFan',
      label: 'พัดเหล็กกล้า',
      ahead: 9,
      behind: 1,
      colours: { ...BLACK, blade: '#9aa2b0', edge: '#3a3e48' },
      form: { head: 'iron' },
    },
    {
      id: 'featherFan',
      label: 'พัดขนนกกระเรียน',
      ahead: 9,
      behind: 1,
      colours: { ...BONE, blade: '#f8f8f4', edge: '#b8b8b0' },
      form: { head: 'feather' },
    },
    {
      id: 'redFan',
      label: 'พัดชาดหงส์',
      ahead: 8,
      behind: 1,
      colours: { ...RED_LACQUER, blade: '#c8303a', edge: '#e8c27e' },
    },
    {
      id: 'frostFan',
      label: 'พัดหิมะพันลี้',
      ahead: 9,
      behind: 1,
      colours: { ...FROST, blade: '#eef8ff', edge: '#7ab0d8' },
      form: { head: 'iron', glow: '#cfeaff' },
    },
    {
      id: 'bladeFan',
      label: 'พัดใบมีดพญามาร',
      ahead: 9,
      behind: 1,
      colours: { ...VIOLET, blade: '#3a2a4a', edge: '#c8b0f0' },
      form: { head: 'iron', glow: '#b06aff' },
    },
    // A war fan: dark oiled-paper leaf on polished steel ribs.
    {
      id: 'warFan',
      label: 'พัดศึกซี่เหล็ก',
      ahead: 9,
      behind: 1,
      colours: { blade: '#5a4a3e', edge: '#2e241e', guard: '#c8ced8', grip: '#3a3e48', tassel: '#9a2e2a' },
      form: { head: 'iron' },
    },
  ]),
  // ── กริช (kris): a wavy blade ──
  ...family('kris', 'right', [
    { id: 'kris', label: 'กริช', ahead: 10, behind: 3, colours: { ...STEEL, grip: '#6e4a2c' } },
    {
      id: 'kris5',
      label: 'กริชห้าคด',
      ahead: 12,
      behind: 3,
      colours: { ...STEEL, grip: '#6e4a2c' },
      form: { waves: 5 },
    },
    {
      id: 'kris7',
      label: 'กริชเจ็ดคด',
      ahead: 13,
      behind: 3,
      colours: { ...BLACK, grip: '#4a2a1a' },
      form: { waves: 7, inlay: '#e8c27e' },
    },
    {
      id: 'kris9',
      label: 'กริชเก้าคด',
      ahead: 14,
      behind: 3,
      colours: { ...STEEL, grip: '#3a2416', guard: '#f0c040' },
      form: { waves: 9, width: 1.2 },
    },
    {
      id: 'straightKris',
      label: 'กริชตรง',
      ahead: 11,
      behind: 3,
      colours: { ...STEEL, grip: '#6e4a2c' },
      form: { waves: 0 },
    },
    {
      id: 'goldKris',
      label: 'กริชทองราชสำนัก',
      ahead: 12,
      behind: 3,
      colours: GOLD,
      form: { waves: 5, ridge: true },
    },
    { id: 'boneKris', label: 'กริชกระดูก', ahead: 11, behind: 3, colours: BONE, form: { waves: 3 } },
    {
      id: 'jadeKris',
      label: 'กริชหยกพราย',
      ahead: 12,
      behind: 3,
      colours: JADE,
      form: { waves: 5, glow: '#9ae8c0' },
    },
    {
      id: 'bloodKris',
      label: 'กริชดูดเลือด',
      ahead: 13,
      behind: 3,
      colours: CRIMSON,
      form: { waves: 7, glow: '#ff5a3a' },
    },
    {
      id: 'shadowKris',
      label: 'กริชเงามาร',
      ahead: 13,
      behind: 3,
      colours: VIOLET,
      form: { waves: 9, inlay: '#b06aff', glow: '#b06aff' },
    },
  ]),
  // ── กระบอง (staff) ──
  ...family('staff', 'right', [
    { id: 'staff', label: 'กระบอง', ahead: 15, behind: 15, colours: WOOD, form: { head: 'caps' } },
    {
      id: 'bambooStaff',
      label: 'ไม้เท้าไผ่',
      ahead: 15,
      behind: 15,
      colours: BAMBOO,
      form: { head: 'plain', bands: 4 },
    },
    {
      id: 'ironStaff',
      label: 'กระบองเหล็ก',
      ahead: 15,
      behind: 15,
      colours: BLACK,
      form: { head: 'caps', bands: 2 },
    },
    {
      id: 'redStaff',
      label: 'กระบองชาด',
      ahead: 15,
      behind: 15,
      colours: RED_LACQUER,
      form: { head: 'caps' },
    },
    {
      id: 'ebonyStaff',
      label: 'กระบองไม้มะเกลือ',
      ahead: 15,
      behind: 15,
      colours: EBONY,
      form: { head: 'caps', bands: 2 },
    },
    {
      id: 'monkStaff',
      label: 'ไม้เท้าห่วงพระ',
      ahead: 17,
      behind: 14,
      colours: pole(BRONZE, WOOD),
      form: { head: 'rings', rings: 6 },
    },
    {
      id: 'dragonStaff',
      label: 'ไม้เท้าหัวมังกร',
      ahead: 16,
      behind: 14,
      colours: pole(GOLD, EBONY),
      form: { head: 'dragon' },
    },
    {
      id: 'boneStaff',
      label: 'ไม้เท้ากระดูก',
      ahead: 16,
      behind: 14,
      colours: BONE,
      form: { head: 'skull' },
    },
    {
      id: 'goldCudgel',
      label: 'กระบองทองหยู่อี้',
      ahead: 16,
      behind: 16,
      colours: { ...RED_LACQUER, guard: '#f0c040' },
      form: { head: 'golden', glow: '#ffe08a' },
    },
    {
      id: 'jadeStaff',
      label: 'ไม้เท้าหยกเซียน',
      ahead: 16,
      behind: 14,
      colours: pole(JADE, BAMBOO),
      form: { head: 'orb', glow: '#9ae8c0' },
    },
  ]),
  // ── หนาม (emei piercers) ──
  ...family('spike', 'pair', [
    { id: 'spike', label: 'หนาม', ahead: 6, behind: 6, colours: STEEL },
    { id: 'blackSpike', label: 'หนามเหล็กดำ', ahead: 6, behind: 6, colours: BLACK },
    { id: 'bronzeSpike', label: 'หนามสัมฤทธิ์', ahead: 6, behind: 6, colours: BRONZE },
    { id: 'goldSpike', label: 'หนามทองเอ๋อเหมย', ahead: 7, behind: 7, colours: GOLD },
    { id: 'jadeSpike', label: 'หนามหยก', ahead: 6, behind: 6, colours: JADE },
    { id: 'silverSpike', label: 'หนามเงินจันทร์', ahead: 7, behind: 7, colours: SILVER },
    { id: 'longSpike', label: 'หนามยาวปักฟ้า', ahead: 9, behind: 7, colours: STEEL },
    {
      id: 'frostSpike',
      label: 'หนามน้ำแข็ง',
      ahead: 7,
      behind: 7,
      colours: FROST,
      form: { glow: '#cfeaff' },
    },
    {
      id: 'bloodSpike',
      label: 'หนามโลหิต',
      ahead: 7,
      behind: 7,
      colours: CRIMSON,
      form: { glow: '#ff5a3a' },
    },
    {
      id: 'shadowSpike',
      label: 'หนามเงามาร',
      ahead: 8,
      behind: 7,
      colours: VIOLET,
      form: { glow: '#b06aff' },
    },
  ]),
  // ── โซ่ (chain weapons) ──
  ...family('chain', 'right', [
    {
      id: 'chain',
      label: 'โซ่',
      ahead: 24,
      behind: 0,
      colours: { ...STEEL, blade: '#9fa8b8', edge: '#5a6270' },
      form: { head: 'dart' },
    },
    {
      id: 'meteorHammer',
      label: 'ลูกตุ้มดาวตก',
      ahead: 24,
      behind: 0,
      colours: { ...BLACK, blade: '#7a808c', edge: '#3a3e48' },
      form: { head: 'ball' },
    },
    {
      id: 'ropeDart',
      label: 'เชือกลูกดอก',
      ahead: 24,
      behind: 0,
      colours: { ...STEEL, blade: '#c8b48a', edge: '#8a7458' },
      form: { head: 'dart' },
    },
    {
      id: 'nineSection',
      label: 'แส้เหล็กเก้าท่อน',
      ahead: 24,
      behind: 0,
      colours: STEEL,
      form: { head: 'sections' },
    },
    {
      id: 'sickleChain',
      label: 'โซ่เคียว',
      ahead: 24,
      behind: 0,
      colours: { ...STEEL, blade: '#9fa8b8', edge: '#5a6270' },
      form: { head: 'sickle' },
    },
    {
      id: 'goldChain',
      label: 'โซ่ทองพันธนาการ',
      ahead: 24,
      behind: 0,
      colours: { ...GOLD, blade: '#e8c27e', edge: '#a8782a' },
      form: { head: 'dart' },
    },
    {
      id: 'bronzeMeteor',
      label: 'ตุ้มสัมฤทธิ์',
      ahead: 24,
      behind: 0,
      colours: BRONZE,
      form: { head: 'ball', size: 1.2 },
    },
    {
      id: 'jadeChain',
      label: 'โซ่หยกเย็น',
      ahead: 24,
      behind: 0,
      colours: JADE,
      form: { head: 'dart', glow: '#9ae8c0' },
    },
    {
      id: 'flameMeteor',
      label: 'ตุ้มเพลิงอัคคี',
      ahead: 24,
      behind: 0,
      colours: CRIMSON,
      form: { head: 'ball', size: 1.3, glow: '#ff7a3a' },
    },
    {
      id: 'soulChain',
      label: 'โซ่ตรึงวิญญาณ',
      ahead: 26,
      behind: 0,
      colours: VIOLET,
      form: { head: 'sickle', glow: '#b06aff' },
    },
  ]),
  // ── ง้าว (guandao): a broad crescent blade on a long shaft ──
  ...family('glaive', 'right', [
    { id: 'guandao', label: 'ง้าวจันทร์เสี้ยว', ahead: 22, behind: 14, colours: SPEAR },
    {
      id: 'greenDragon',
      label: 'ง้าวมังกรเขียว',
      ahead: 23,
      behind: 14,
      colours: pole(JADE, EBONY, '#2f8a5a'),
      form: { size: 1.3, guard: 'beast', glow: '#9ae8c0' },
    },
    {
      id: 'phoenixGlaive',
      label: 'ง้าวจะงอยหงส์',
      ahead: 22,
      behind: 14,
      colours: pole(STEEL, RED_LACQUER),
      form: { size: 1.1, head: 'beak' },
    },
    {
      id: 'blackGlaive',
      label: 'ง้าวเหล็กดำ',
      ahead: 22,
      behind: 14,
      colours: pole(BLACK, BLACK, '#2a2a34'),
      form: { size: 1.2 },
    },
    {
      id: 'bronzeGlaive',
      label: 'ง้าวสัมฤทธิ์',
      ahead: 22,
      behind: 14,
      colours: pole(BRONZE, WOOD),
      form: { size: 1.1, bands: 2 },
    },
    {
      id: 'bigGlaive',
      label: 'ง้าวใหญ่ทลายทัพ',
      ahead: 24,
      behind: 15,
      colours: SPEAR,
      form: { size: 1.5, teeth: true },
    },
    {
      id: 'moonGlaive',
      label: 'ง้าวจันทร์เงิน',
      ahead: 22,
      behind: 14,
      colours: pole(SILVER, SILVER, '#e8ecf4'),
      form: { size: 1.2, glow: '#e8f0ff' },
    },
    {
      id: 'goldGlaive',
      label: 'ง้าวทองแม่ทัพ',
      ahead: 23,
      behind: 14,
      colours: pole(GOLD, RED_LACQUER),
      form: { size: 1.3, guard: 'beast', bands: 2 },
    },
    {
      id: 'bloodGlaive',
      label: 'ง้าวโลหิตสงคราม',
      ahead: 23,
      behind: 14,
      colours: pole(CRIMSON, EBONY, '#8a1420'),
      form: { size: 1.3, teeth: true, glow: '#ff5a3a' },
    },
    {
      id: 'ghostGlaive',
      label: 'ง้าวยมทูต',
      ahead: 24,
      behind: 14,
      colours: pole(VIOLET, BLACK, '#3a1a6a'),
      form: { size: 1.4, head: 'beak', glow: '#b06aff' },
    },
  ]),
  // ── ทวนเสี้ยวจันทร์ (ji): a spear point with crescent blades ──
  ...family('halberd', 'right', [
    { id: 'ji', label: 'ทวนเสี้ยวจันทร์', ahead: 22, behind: 14, colours: SPEAR },
    {
      id: 'skyJi',
      label: 'ทวนฟ้าทะลวงฟ้า',
      ahead: 24,
      behind: 15,
      colours: pole(STEEL, RED_LACQUER),
      form: { head: 'double', size: 1.2, tassel: 'long' },
    },
    {
      id: 'blackJi',
      label: 'ทวนเสี้ยวเหล็กดำ',
      ahead: 22,
      behind: 14,
      colours: pole(BLACK, BLACK),
      form: { size: 1.1 },
    },
    {
      id: 'bronzeJi',
      label: 'ทวนเสี้ยวสัมฤทธิ์',
      ahead: 22,
      behind: 14,
      colours: pole(BRONZE, WOOD),
      form: { head: 'double', bands: 2 },
    },
    {
      id: 'goldJi',
      label: 'ทวนเสี้ยวทองคำ',
      ahead: 23,
      behind: 14,
      colours: pole(GOLD, RED_LACQUER),
      form: { head: 'double', size: 1.2, tassel: 'long' },
    },
    {
      id: 'jadeJi',
      label: 'ทวนเสี้ยวหยก',
      ahead: 22,
      behind: 14,
      colours: pole(JADE, EBONY),
      form: { size: 1.1 },
    },
    {
      id: 'silverJi',
      label: 'ทวนเสี้ยวเงิน',
      ahead: 23,
      behind: 14,
      colours: pole(SILVER, SILVER),
      form: { head: 'double', tassel: 'long' },
    },
    {
      id: 'axeJi',
      label: 'ทวนขวาน',
      ahead: 22,
      behind: 14,
      colours: SPEAR,
      form: { head: 'axe', size: 1.2 },
    },
    {
      id: 'thunderJi',
      label: 'ทวนเสี้ยวอัสนี',
      ahead: 24,
      behind: 15,
      colours: pole(AZURE, BLACK, '#3a7ae0'),
      form: { head: 'double', size: 1.2, glow: '#9ad0ff' },
    },
    {
      id: 'demonJi',
      label: 'ทวนเสี้ยวอสูร',
      ahead: 24,
      behind: 15,
      colours: pole(VIOLET, BLACK, '#8a3ad8'),
      form: { head: 'double', size: 1.3, teeth: true, glow: '#b06aff' },
    },
  ]),
  // ── ขวาน (axe) ──
  ...family('axe', 'right', [
    { id: 'axe', label: 'ขวานศึก', ahead: 13, behind: 3, colours: { ...STEEL, grip: '#6e4a2c' } },
    {
      id: 'woodAxe',
      label: 'ขวานตัดฟืน',
      ahead: 12,
      behind: 3,
      colours: { ...STEEL, grip: '#8a5a32' },
      form: { head: 'broad', size: 0.9 },
    },
    {
      id: 'moonAxe',
      label: 'ขวานจันทร์เสี้ยว',
      ahead: 14,
      behind: 3,
      colours: { ...STEEL, grip: '#3a2416' },
      form: { head: 'moon', size: 1.2 },
    },
    {
      id: 'doubleAxe',
      label: 'ขวานสองคม',
      ahead: 14,
      behind: 3,
      colours: { ...STEEL, grip: '#3a2416' },
      form: { head: 'double' },
    },
    {
      id: 'blackAxe',
      label: 'ขวานเหล็กดำ',
      ahead: 14,
      behind: 3,
      colours: { ...BLACK, grip: '#1a1418' },
      form: { head: 'bearded', size: 1.2 },
    },
    {
      id: 'bronzeAxe',
      label: 'ขวานสัมฤทธิ์',
      ahead: 13,
      behind: 3,
      colours: { ...BRONZE, grip: '#4a2a1a' },
      form: { head: 'moon' },
    },
    {
      id: 'giantAxe',
      label: 'ขวานยักษ์ผ่าภูเขา',
      ahead: 16,
      behind: 4,
      colours: { ...STEEL, grip: '#3a2416' },
      form: { head: 'moon', size: 1.6 },
    },
    {
      id: 'goldAxe',
      label: 'ขวานทองเทพสงคราม',
      ahead: 15,
      behind: 3,
      colours: { ...GOLD, grip: '#5a1a14' },
      form: { head: 'double', size: 1.2, glow: '#ffe08a' },
    },
    {
      id: 'bloodAxe',
      label: 'ขวานโลหิตพยัคฆ์',
      ahead: 15,
      behind: 3,
      colours: { ...CRIMSON, grip: '#2a0a0e' },
      form: { head: 'bearded', size: 1.3, teeth: true, glow: '#ff5a3a' },
    },
    {
      id: 'frostAxe',
      label: 'ขวานน้ำแข็งเหนือ',
      ahead: 15,
      behind: 3,
      colours: { ...FROST, grip: '#2a3a5a' },
      form: { head: 'double', size: 1.3, glow: '#cfeaff' },
    },
  ]),
  // ── ค้อน (hammers) ──
  ...family('hammer', 'pair', [
    {
      id: 'hammers',
      label: 'ค้อนคู่',
      ahead: 9,
      behind: 2,
      colours: { ...STEEL, blade: '#9aa2ae', edge: '#5a6270', grip: '#6e4a2c' },
    },
    {
      id: 'melonHammers',
      label: 'ค้อนแตงทอง',
      ahead: 9,
      behind: 2,
      colours: { ...GOLD, grip: '#5a1a14' },
      form: { head: 'melon' },
    },
    {
      id: 'octaHammers',
      label: 'ค้อนแปดเหลี่ยม',
      ahead: 9,
      behind: 2,
      colours: { ...BLACK, grip: '#3a2416' },
      form: { head: 'octa' },
    },
    {
      id: 'spikedHammers',
      label: 'ค้อนหนามเหล็ก',
      ahead: 9,
      behind: 2,
      colours: { ...BLACK, grip: '#3a2416' },
      form: { head: 'spiked' },
    },
    {
      id: 'bronzeHammers',
      label: 'ค้อนสัมฤทธิ์',
      ahead: 9,
      behind: 2,
      colours: { ...BRONZE, grip: '#4a2a1a' },
      form: { head: 'melon' },
    },
    {
      id: 'squareHammers',
      label: 'ค้อนเหลี่ยมทลายกำแพง',
      ahead: 9,
      behind: 2,
      colours: { ...STEEL, blade: '#9aa2ae', edge: '#5a6270', grip: '#3a2416' },
      form: { head: 'square', size: 1.2 },
    },
    {
      id: 'bigHammers',
      label: 'ค้อนยักษ์',
      ahead: 10,
      behind: 2,
      colours: { ...STEEL, blade: '#9aa2ae', edge: '#5a6270', grip: '#3a2416' },
      form: { head: 'melon', size: 1.4 },
    },
    {
      id: 'thunderHammers',
      label: 'ค้อนสายฟ้า',
      ahead: 10,
      behind: 2,
      colours: { ...AZURE, grip: '#1c2a4a' },
      form: { head: 'octa', size: 1.2, glow: '#9ad0ff' },
    },
    {
      id: 'lionHammers',
      label: 'ค้อนหัวสิงห์',
      ahead: 10,
      behind: 2,
      colours: { ...GOLD, grip: '#5a1a14' },
      form: { head: 'spiked', size: 1.3, glow: '#ffe08a' },
    },
    {
      id: 'skullHammers',
      label: 'ค้อนกะโหลกมาร',
      ahead: 10,
      behind: 2,
      colours: { ...BONE, grip: '#1a0e2a' },
      form: { head: 'skull', size: 1.2, glow: '#b06aff' },
    },
  ]),
  // ── แส้ปัดฝุ่น (whisk) ──
  ...family('whisk', 'right', [
    {
      id: 'whisk',
      label: 'แส้ปัดฝุ่น',
      ahead: 7,
      behind: 2,
      colours: { ...WOOD, blade: '#f0ece2', edge: '#c9bfa8', grip: '#8a5a32' },
    },
    {
      id: 'blackWhisk',
      label: 'แส้หางม้าดำ',
      ahead: 7,
      behind: 2,
      colours: { ...EBONY, blade: '#3a3438', edge: '#1a1418' },
    },
    {
      id: 'jadeWhisk',
      label: 'แส้ด้ามหยก',
      ahead: 7,
      behind: 2,
      colours: { ...JADE, blade: '#f4f4ee', edge: '#c8c8c0', grip: '#7ac8a0' },
    },
    {
      id: 'goldWhisk',
      label: 'แส้ด้ามทอง',
      ahead: 7,
      behind: 2,
      colours: { ...GOLD, blade: '#f4f0e8', edge: '#d8ccb0', grip: '#e8c27e' },
    },
    {
      id: 'redWhisk',
      label: 'แส้พู่แดง',
      ahead: 7,
      behind: 2,
      colours: { ...WOOD, blade: '#c8303a', edge: '#7a1820' },
    },
    {
      id: 'silverWhisk',
      label: 'แส้เงินยวง',
      ahead: 8,
      behind: 2,
      colours: { ...SILVER, blade: '#e8ecf4', edge: '#a8b0c0' },
      form: { size: 1.2 },
    },
    {
      id: 'ironWhisk',
      label: 'แส้ลวดเหล็ก',
      ahead: 8,
      behind: 2,
      colours: { ...BLACK, blade: '#9aa2b0', edge: '#5a5e6a' },
    },
    {
      id: 'cloudWhisk',
      label: 'แส้เมฆขาวเซียน',
      ahead: 8,
      behind: 2,
      colours: { ...BONE, blade: '#ffffff', edge: '#d8e4f0' },
      form: { size: 1.3, glow: '#e8f4ff' },
    },
    {
      id: 'phoenixWhisk',
      label: 'แส้ขนหงส์',
      ahead: 8,
      behind: 2,
      colours: { ...GOLD, blade: '#ff9a4a', edge: '#c8402a' },
      form: { size: 1.2, glow: '#ff9a4a' },
    },
    {
      id: 'ghostWhisk',
      label: 'แส้วิญญาณ',
      ahead: 8,
      behind: 2,
      colours: { ...VIOLET, blade: '#d8c8f4', edge: '#6a4aa8' },
      form: { size: 1.3, glow: '#b06aff' },
    },
  ]),
  // ── ขอเกี่ยว (hook swords) ──
  ...family('hook', 'pair', [
    { id: 'hooks', label: 'ขอเกี่ยวคู่', ahead: 15, behind: 3, colours: STEEL },
    {
      id: 'tigerHooks',
      label: 'ขอหัวพยัคฆ์',
      ahead: 15,
      behind: 3,
      colours: { ...STEEL, guard: '#f0c040' },
      form: { width: 1.3, size: 1.2 },
    },
    { id: 'blackHooks', label: 'ขอเหล็กดำ', ahead: 15, behind: 3, colours: BLACK },
    { id: 'bronzeHooks', label: 'ขอสัมฤทธิ์', ahead: 15, behind: 3, colours: BRONZE },
    { id: 'moonHooks', label: 'ขอจันทร์คู่', ahead: 16, behind: 3, colours: SILVER, form: { size: 1.3 } },
    { id: 'goldHooks', label: 'ขอทองคู่', ahead: 15, behind: 3, colours: GOLD, form: { width: 1.2 } },
    { id: 'jadeHooks', label: 'ขอหยกคู่', ahead: 15, behind: 3, colours: JADE },
    {
      id: 'frostHooks',
      label: 'ขอน้ำแข็ง',
      ahead: 16,
      behind: 3,
      colours: FROST,
      form: { size: 1.2, glow: '#cfeaff' },
    },
    {
      id: 'bloodHooks',
      label: 'ขอโลหิตเกี่ยววิญญาณ',
      ahead: 16,
      behind: 3,
      colours: CRIMSON,
      form: { teeth: true, glow: '#ff5a3a' },
    },
    {
      id: 'demonHooks',
      label: 'ขออสูรคู่',
      ahead: 17,
      behind: 3,
      colours: VIOLET,
      form: { size: 1.4, glow: '#b06aff' },
    },
  ]),
  // ── แส้ (whips) ──
  ...family('whip', 'right', [
    {
      id: 'whip',
      label: 'แส้หนัง',
      ahead: 22,
      behind: 2,
      colours: { ...WOOD, blade: '#6a4a32', edge: '#4a3020' },
    },
    {
      id: 'blackWhip',
      label: 'แส้หนังดำ',
      ahead: 22,
      behind: 2,
      colours: { ...EBONY, blade: '#3a2e2a', edge: '#1a1210' },
    },
    {
      id: 'redWhip',
      label: 'แส้ชาด',
      ahead: 22,
      behind: 2,
      colours: { ...WOOD, blade: '#a8303a', edge: '#5a1018' },
    },
    {
      id: 'thornWhip',
      label: 'แส้หนาม',
      ahead: 22,
      behind: 2,
      colours: { ...WOOD, blade: '#5a4a2a', edge: '#2a2010' },
      form: { teeth: true },
    },
    {
      id: 'steelWhip',
      label: 'แส้เหล็กข้อปล้อง',
      ahead: 14,
      behind: 3,
      colours: { ...BLACK, blade: '#7a808c', edge: '#3a3e48' },
      form: { head: 'rod' },
    },
    { id: 'bronzeWhip', label: 'แส้สัมฤทธิ์', ahead: 14, behind: 3, colours: BRONZE, form: { head: 'rod' } },
    {
      id: 'goldWhip',
      label: 'แส้ทองพันลี้',
      ahead: 24,
      behind: 2,
      colours: { ...GOLD, blade: '#e8c27e', edge: '#a8782a' },
    },
    {
      id: 'serpentWhip',
      label: 'แส้อสรพิษ',
      ahead: 24,
      behind: 2,
      colours: { ...JADE, blade: '#3a8a5a', edge: '#1a4a30' },
      form: { teeth: true, glow: '#9ae8c0' },
    },
    {
      id: 'fireWhip',
      label: 'แส้เพลิงนรก',
      ahead: 24,
      behind: 2,
      colours: { ...CRIMSON, blade: '#e8502a', edge: '#8a1420' },
      form: { glow: '#ff7a3a' },
    },
    {
      id: 'soulWhip',
      label: 'แส้กระดูกวิญญาณ',
      ahead: 24,
      behind: 2,
      colours: { ...BONE, blade: '#ece2c8', edge: '#6a4aa8' },
      form: { head: 'bone', glow: '#b06aff' },
    },
  ]),
  // ── ขลุ่ย (flute) ──
  ...family('flute', 'right', [
    {
      id: 'jadeFlute',
      label: 'ขลุ่ยหยก',
      ahead: 11,
      behind: 2,
      colours: { ...WOOD, blade: '#7fd0a8', edge: '#3f8a68' },
    },
    {
      id: 'bambooFlute',
      label: 'ขลุ่ยไผ่',
      ahead: 11,
      behind: 2,
      colours: { ...BAMBOO, blade: '#d8c870', edge: '#8a7a2a' },
    },
    {
      id: 'blackFlute',
      label: 'ขลุ่ยไผ่ดำ',
      ahead: 11,
      behind: 2,
      colours: { ...EBONY, blade: '#3a3430', edge: '#1a1410' },
    },
    { id: 'boneFlute', label: 'ขลุ่ยกระดูก', ahead: 10, behind: 2, colours: BONE },
    {
      id: 'whiteJadeFlute',
      label: 'ขลุ่ยหยกขาว',
      ahead: 11,
      behind: 2,
      colours: { ...WOOD, blade: '#f0f4ee', edge: '#a8b8a8', tassel: '#3a7ae0' },
    },
    {
      id: 'goldFlute',
      label: 'ขลุ่ยทอง',
      ahead: 11,
      behind: 2,
      colours: { ...GOLD, blade: '#f0d080', edge: '#a8782a' },
    },
    {
      id: 'ironFlute',
      label: 'ขลุ่ยเหล็กสยบมาร',
      ahead: 12,
      behind: 2,
      colours: { ...BLACK, blade: '#7a808c', edge: '#3a3e48' },
    },
    {
      id: 'redFlute',
      label: 'ขลุ่ยชาด',
      ahead: 11,
      behind: 2,
      colours: { ...RED_LACQUER, blade: '#c8402a', edge: '#6a1410' },
    },
    {
      id: 'frostFlute',
      label: 'ขลุ่ยน้ำแข็งพันปี',
      ahead: 12,
      behind: 2,
      colours: { ...FROST, blade: '#d8f0ff', edge: '#6aa0c8' },
      form: { glow: '#cfeaff' },
    },
    {
      id: 'phoenixFlute',
      label: 'ขลุ่ยหงส์ร้อง',
      ahead: 12,
      behind: 2,
      colours: { ...GOLD, blade: '#ff9a4a', edge: '#a8402a' },
      form: { glow: '#ff9a4a' },
    },
  ]),
  // ── พู่กัน (brush) ──
  ...family('brush', 'right', [
    {
      id: 'brush',
      label: 'พู่กันเหล็ก',
      ahead: 13,
      behind: 2,
      colours: { ...WOOD, blade: '#1a1418', edge: '#3a3438', grip: '#8a5a32' },
    },
    {
      id: 'judgeBrush',
      label: 'พู่กันพิพากษา',
      ahead: 14,
      behind: 2,
      colours: { ...EBONY, blade: '#1a1418', edge: '#3a3438', guard: '#f0c040' },
      form: { size: 1.2 },
    },
    {
      id: 'jadeBrush',
      label: 'พู่กันด้ามหยก',
      ahead: 13,
      behind: 2,
      colours: { ...JADE, blade: '#1a1418', edge: '#3a3438', grip: '#7ac8a0' },
    },
    {
      id: 'goldBrush',
      label: 'พู่กันทอง',
      ahead: 13,
      behind: 2,
      colours: { ...GOLD, blade: '#1a1418', edge: '#3a3438', grip: '#e8c27e' },
    },
    {
      id: 'whiteBrush',
      label: 'พู่กันขนขาว',
      ahead: 13,
      behind: 2,
      colours: { ...WOOD, blade: '#f4f0e8', edge: '#c8c0b0', grip: '#5e3c22' },
    },
    {
      id: 'redBrush',
      label: 'พู่กันชาด',
      ahead: 13,
      behind: 2,
      colours: { ...RED_LACQUER, blade: '#c8202a', edge: '#5a1010', grip: '#1a1210' },
    },
    {
      id: 'bambooBrush',
      label: 'พู่กันไผ่',
      ahead: 12,
      behind: 2,
      colours: { ...BAMBOO, blade: '#1a1418', edge: '#3a3438' },
    },
    {
      id: 'greatBrush',
      label: 'พู่กันใหญ่ปัดเมฆ',
      ahead: 15,
      behind: 3,
      colours: { ...EBONY, blade: '#1a1418', edge: '#3a3438' },
      form: { size: 1.5 },
    },
    {
      id: 'starBrush',
      label: 'พู่กันลิขิตดาว',
      ahead: 14,
      behind: 2,
      colours: { ...AZURE, blade: '#2a3a6a', edge: '#9ad0ff', grip: '#d8e4f4' },
      form: { size: 1.2, glow: '#9ad0ff' },
    },
    {
      id: 'fateBrush',
      label: 'พู่กันลิขิตชะตา',
      ahead: 15,
      behind: 2,
      colours: { ...VIOLET, blade: '#2a1a3a', edge: '#c8b0f0', grip: '#e8c27e' },
      form: { size: 1.3, glow: '#b06aff' },
    },
  ]),
];

export const WEAPON_BY_ID: Readonly<Record<string, WeaponDesign>> = Object.fromEntries(
  WEAPONS.map((w) => [w.id, w]),
);

/** Whether the hand on `side` closes round a grip with this weapon. */
export function grips(w: WeaponDesign | undefined, side: Side): boolean {
  if (!w) return false;
  if (w.hands === 'pair') return true;
  if (w.hands === 'left') return side === 'B';
  if (w.hands === 'right') return side === 'A';
  return false;
}

/** Blade angles for right-arm poses the classic figure drew as blocks rather than limbs. */
const OTHER_ANGLE: Partial<Record<Rig['arm'], number>> = {
  // Held loose at the side, pointing down and a little ahead.
  hang: 1.06,
  forward: -1.17,
  guard: -0.67,
  raise: -2.36,
  // Laid across the lap.
  sit: Math.PI,
  // Held point-up behind the clasped hands of a salute.
  clasp: -1.4,
};
/** Blade angles for the left arm's poses. */
const FAR_ANGLE: Record<Rig['far'], number> = {
  hang: 1.06,
  back: 2.6,
  // Reaching over the rear shoulder to a sword worn on the back (F2): as `back` until it gets its own angle.
  overBack: 2.6,
  fwd: 0.3,
  up: -1.9,
  clasp: -1.4,
  hip: 1.06,
  fold: 0,
  face: -1.4,
  clap: -1.4,
  clapOpen: -1.4,
  // The reversed blade along the outside of the off forearm, point up by the shoulder (motions/hold.ts).
  revArm: -Math.PI / 2 - 0.3,
};

/** How far out to its arm's side a blade pointed at (or away from) the camera is canted, head-on (`weaponAim`). */
const BLADE_OUT = 0.6;

/**
 * Where the weapon in the hand on `side` points (radians, screen space) and whether it is behind
 * the body. Head-on, a sideways swing is mirrored with the right arm on the screen's left, as the
 * arm's reach is.
 */
export function weaponAim(
  sk: Skeleton,
  side: Side = 'A',
): { angle: number; behind: boolean; fore: number; ahead: number } {
  const { rig } = sk;
  // An in-between frame: the blade turned part of the way to where the next position holds it.
  if (side === 'A' && rig.armTo && rig.armT) {
    const from = weaponAim({ ...sk, rig: { ...rig, armTo: undefined } }, side);
    const to = weaponAim({ ...sk, rig: { ...rig, arm: rig.armTo, armTo: undefined } }, side);
    // The short way round, unless that is nearly half a turn against the hand's own swing: then
    // the blade goes round with the hand (over the head into an overhead cut, not down through
    // the body).
    let turn = Math.atan2(Math.sin(to.angle - from.angle), Math.cos(to.angle - from.angle));
    if (rig.armWay && Math.abs(turn) > 2 && Math.sign(turn) !== rig.armWay)
      turn -= Math.sign(turn) * Math.PI * 2;
    return {
      ...from,
      angle: from.angle + turn * rig.armT,
      fore: from.fore + (to.fore - from.fore) * rig.armT,
      ahead: from.ahead + (to.ahead - from.ahead) * rig.armT,
    };
  }
  let angle: number;
  let behind = false;
  if (side === 'A') {
    const lim = LIMB[rig.arm];
    angle = lim?.ang ?? OTHER_ANGLE[rig.arm] ?? 1.06;
    behind = lim ? !lim.front : false;
    // Walking and running, the blade swings with the arm: ahead as the hand comes forward
    // (pointing more forward), back as it goes back.
    angle -= rig.armSwing * 0.09;
  } else {
    angle = FAR_ANGLE[rig.far];
    // The reversed blade along the off forearm (`revArm`), seen with that arm on the far side (right-facing views):
    // canted further back so the blade shows past the back instead of hiding behind the body (review R1).
    if (rig.far === 'revArm' && !sk.square && !sk.left) angle -= 0.3;
  }
  // revArm seen from the front and three-quarter (review R2): the forearm turned out so the blade stands outside the
  // arm's silhouette up to the shoulder, not hidden behind the body.
  if (side === 'B' && rig.far === 'revArm' && sk.view !== 'side') {
    const out = Math.sign(sk.joints.handB.x - sk.joints.chest.x) || 1;
    angle = Math.atan2(Math.sin(angle), Math.cos(angle) + out * 0.35);
  }
  const onLeft = Math.sign(sk.joints[`shoulder${side}`].x - sk.joints.chest.x) < 0;
  /** How much of the blade points the way the figure faces (−1: straight back), for its depth. */
  const ahead = Math.cos(angle);
  // Head-on, a weapon arm reaching along the line of sight carries its blade the same way: the
  // profile's direction seen down the camera, forward turned into depth (lower on the screen toward
  // the camera, higher away from it), a little out to the arm's side, and shortened by it. So does
  // a blade hanging loose at the side, pointing down and ahead: from behind it runs away past the
  // hip, short and nearly upright, instead of sticking out sideways across the robe.
  const hanging = side === 'A' ? rig.arm === 'hang' : rig.far === 'hang';
  if (sk.square && ((side === 'A' && sk.foreA) || hanging)) {
    const fx = Math.cos(angle);
    // A blade held out toward the camera is canted out to its arm's side by the wrist (lane C0-WEAPON): run
    // straight down the line of sight it shrank to a stub hanging by the leg, and a levelled guard or a
    // cut read as a sword let drop. A hanging blade keeps the arm's own spread.
    const out = hanging ? SEEN_OUT : BLADE_OUT;
    const vx = (onLeft ? -1 : 1) * Math.abs(fx) * out;
    const vy = Math.sin(angle) + fx * SEEN_DEPTH * (sk.view === 'front' ? 1 : -1);
    return {
      angle: Math.atan2(vy, vx),
      behind,
      fore: Math.max(hanging ? 0.35 : 0.5, Math.min(1, Math.hypot(vx, vy))),
      ahead,
    };
  }
  if (sk.square && (side === 'A' ? onLeft : !onLeft)) angle = Math.PI - angle;
  return { angle, behind, fore: 1, ahead };
}

const hex = (c: string) => [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16));
/** A colour `k` of the way from `a` to `b`. */
function mix(a: string, b: string, k: number): string {
  const A = hex(a);
  const B = hex(b);
  return `#${A.map((v, i) =>
    Math.round(v + (B[i]! - v) * k)
      .toString(16)
      .padStart(2, '0'),
  ).join('')}`;
}

/** A metal's tones from its light and dark: a white glint, the light, the middle, the dark. */
const metal = (light: string, dark: string) => ({
  hi: mix(light, '#ffffff', 0.65),
  light,
  mid: mix(light, dark, 0.45),
  dark,
});
type Tones = ReturnType<typeof metal>;

function line(g: G, a: Pt, b: Pt, colour: string): void {
  const n = Math.max(1, Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y))));
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    px(g, a.x + (b.x - a.x) * t - 0.5, a.y + (b.y - a.y) * t - 0.5, 1, 1, colour);
  }
}

/** A round lump (a hammer head, an orb, a meteor) lit from the upper right. */
function ball(
  g: G,
  c: Pt,
  r: number,
  t: Tones,
  shape: 'round' | 'octa' | 'square' | 'melon' = 'round',
): void {
  const R = Math.ceil(r);
  for (let y = -R; y <= R; y++)
    for (let x = -R; x <= R; x++) {
      const inside =
        shape === 'square'
          ? Math.abs(x) <= r && Math.abs(y) <= r * 0.85
          : shape === 'octa'
            ? Math.abs(x) + Math.abs(y) <= r * 1.45 && Math.abs(x) <= r && Math.abs(y) <= r
            : shape === 'melon'
              ? (x * x) / (r * r) + (y * y) / (r * r * 0.8) <= 1.05
              : x * x + y * y <= r * r + 0.6;
      if (!inside) continue;
      const lit = (x * 0.6 - y * 0.8) / Math.max(1, r);
      let col = lit > 0.5 ? t.hi : lit > 0 ? t.light : lit > -0.5 ? t.mid : t.dark;
      if (shape === 'melon' && x % 2 === 0 && lit < 0.5) col = t.mid;
      px(g, Math.round(c.x) + x, Math.round(c.y) + y, 1, 1, col);
    }
}

/**
 * Draws a weapon held at `hand`, pointing along `angle`. `open` spreads a fan; `sk` lets a chain
 * sway with the figure's movement.
 */
export function drawWeapon(
  g: G,
  w: WeaponDesign,
  hand: Pt,
  angle: number,
  sk: Skeleton,
  open = false,
  res = 1,
  /** How much of its length shows: below 1 when it points toward or away from the camera. */
  fore = 1,
): void {
  const d = { x: Math.cos(angle), y: Math.sin(angle) };
  const n = { x: -d.y, y: d.x };
  const at = (k: number, side = 0): Pt => ({
    x: hand.x + (d.x * k * fore + n.x * side) * res,
    y: hand.y + (d.y * k * fore + n.y * side) * res,
  });
  const dot = (p: Pt, col: string) => px(g, p.x - 0.5, p.y - 0.5, 1, 1, col);
  const c = w.colours;
  const f = w.form ?? {};
  const size = f.size ?? 1;
  // Which side of the weapon faces the light (upper right): +1 the +n side, −1 the other.
  const lit = n.x * 0.6 - n.y * 0.8 >= 0 ? 1 : -1;
  const steel = metal(c.blade, c.edge);
  const fit = metal(c.guard, shade(c.guard, -0.3));
  /** Fine detail only drawn at higher resolution: a fuller, a gem in the guard, a bead on the tassel. */
  const fine = res >= 2;
  const groove = mix(steel.mid, steel.dark, 0.5);

  /**
   * Fills a strip along the weapon from `k0` to `k1`, between `lo(t)` and `hi(t)` across it, each
   * sample painted by where it lies across (`u`: −1 the shadowed side … 1 the lit side, `p`: 0 at
   * `lo` … 1 at `hi`) and along (`t`: 0 … 1).
   */
  const strip = (
    k0: number,
    k1: number,
    lo: (t: number) => number,
    hi: (t: number) => number,
    paint: (u: number, p: number, t: number, k: number) => string | undefined,
  ) => {
    const len = Math.max(0.01, k1 - k0);
    for (let k = k0; k <= k1 + 1e-6; k += 0.35 / res) {
      const t = (k - k0) / len;
      const a = lo(t);
      const b = hi(t);
      if (b < a) continue;
      for (let s = a; s <= b + 1e-6; s += 0.35 / res) {
        const p = b - a < 0.3 ? 0.5 : (s - a) / (b - a);
        const col = paint((p * 2 - 1) * lit, p, t, k);
        if (col) dot(at(k, s), col);
      }
    }
  };
  const even = (h: (t: number) => number) => [(t: number) => -h(t), h] as const;
  /** Polished metal: a glint on the lit edge, the shadowed edge dark, a bright ridge if it has one. */
  const polished =
    (t: Tones, ridge = false) =>
    (u: number) =>
      ridge && Math.abs(u) < 0.22 ? t.hi : u > 0.55 ? t.hi : u < -0.55 ? t.dark : u > -0.1 ? t.light : t.mid;
  /** Wood: lit and shaded along its round, with grain running down it. */
  const wood = (base: string) => {
    const light = shade(base, 0.08);
    const dark = shade(base, -0.14);
    const grain = shade(base, -0.06);
    return (u: number, _p: number, _t: number, k: number) =>
      u > 0.45 ? light : u < -0.45 ? dark : Math.floor(k * 0.8 + u * 1.5) % 4 === 0 ? grain : base;
  };
  /** A cord-wrapped grip: diamonds of light and dark. */
  const wrapped = (base: string) => {
    const light = shade(base, 0.16);
    return (u: number, _p: number, _t: number, k: number) =>
      (Math.floor(k * 1.4) + (u > 0 ? 1 : 0)) % 2 ? base : light;
  };
  const shaft = (k0: number, k1: number, base: string, half = 0.75) => {
    strip(k0, k1, ...even(() => half), wood(base));
    if (f.bands)
      for (let i = 1; i <= f.bands; i++) {
        const k = k0 + ((k1 - k0) * i) / (f.bands + 1);
        strip(k - 0.4, k + 0.4, ...even(() => half + 0.3), polished(fit));
      }
  };
  /** Sparks of a famed weapon's light along a blade from `k0` to `k1`, `h` from its middle. */
  const glow = (k0: number, k1: number, h: (t: number) => number) => {
    if (!f.glow) return;
    const faint = mix(f.glow, '#ffffff', 0.5);
    for (let k = k0 + 1, i = 0; k <= k1; k += 2.6, i++) {
      const t = (k - k0) / Math.max(1, k1 - k0);
      const side = i % 2 ? 1 : -1;
      dot(at(k, side * (h(t) + 1.4)), i % 3 ? f.glow : faint);
    }
  };
  const grip = () => strip(-w.behind, 0, ...even(() => 0.7), wrapped(c.grip));
  /** A set stone in the middle of the guard (fine detail). */
  const gem = () => {
    if (!fine || f.gem === false || (f.guard ?? 'bar') === 'none') return;
    const p = at(1.2);
    px(g, p.x - 1, p.y - 1, 2, 2, c.tassel);
    px(g, p.x, p.y - 1, 1, 1, mix(c.tassel, '#ffffff', 0.6));
  };
  const guard = (style = f.guard ?? 'bar') => {
    switch (style) {
      case 'none':
        return;
      case 'disc':
        strip(0.4, 2.2, ...even((t) => 2.6 * Math.sin(Math.PI * (0.15 + 0.7 * t))), polished(fit));
        return;
      case 'beast':
        // A beast's head swallowing the blade: bulky, with a glinting eye.
        strip(0, 2.6, ...even((t) => 3 - Math.abs(t - 0.35) * 3), polished(fit));
        dot(at(1.3, 1.6 * lit), c.tassel);
        dot(at(1.3, -1.6 * lit), shade(c.guard, -0.35));
        return;
      case 'wings':
        strip(0.6, 1.8, ...even(() => 2), polished(fit));
        for (const s of [-1, 1]) {
          line(g, at(1.2, s * 2), at(3.6, s * 3.6), s === lit ? fit.hi : fit.mid);
          dot(at(2.6, s * 2.6), fit.dark);
        }
        return;
      case 'crescent':
        strip(0.6, 1.8, ...even(() => 2), polished(fit));
        for (const s of [-1, 1]) line(g, at(1, s * 2), at(-1.2, s * 3.6), s === lit ? fit.hi : fit.mid);
        return;
      case 'cloud':
        strip(0.6, 1.8, ...even(() => 2.6), polished(fit));
        for (const s of [-1, 1]) {
          dot(at(2.4, s * 3.2), fit.light);
          dot(at(1.6, s * 3.6), fit.mid);
          dot(at(0.6, s * 3.2), fit.dark);
        }
        return;
      default:
        strip(0.6, 1.8, ...even(() => 2.6), polished(fit));
    }
  };
  const pommel = (style = f.pommel ?? 'knob') => {
    const k = -w.behind;
    if (style === 'knob') strip(k - 1.3, k, ...even(() => 1.2), polished(fit));
    else if (style === 'cap') strip(k - 0.8, k, ...even(() => 0.9), polished(fit));
    else if (style === 'ring')
      for (let a = 0; a < Math.PI * 2; a += 0.4) {
        const p = at(k - 1.8 + Math.cos(a) * 1.6, Math.sin(a) * 1.6);
        dot(p, Math.sin(a) * lit > 0 ? fit.hi : fit.mid);
      }
  };
  /** A tassel hanging from the pommel (or from point `from`), two strands under a knot. */
  const tassel = (
    len = f.tassel === 'long' ? 6 : 3,
    from = at(-w.behind - (f.pommel === 'ring' ? 3.2 : 1.4)),
  ) => {
    if (f.tassel === 'none') return;
    const dark = shade(c.tassel, -0.2);
    dot(from, shade(c.tassel, 0.1));
    // A bead of the guard's metal over the knot (fine detail).
    if (fine) px(g, from.x - 1, from.y - 1, 2, 2, fit.light);
    for (let i = 1; i <= len * res; i++) {
      const sway = (sk.square ? 0 : flutter(sk, i / (len * res), i * 0.4, 0.5).dx) * res;
      for (let x = 0; x < res * 2; x++)
        dot(
          { x: from.x + (x - res + 0.5) + sway, y: from.y + i },
          i > (len - 2) * res || x === res * 2 - 1 ? dark : c.tassel,
        );
    }
  };
  /**
   * The blade's thickness: a one-pixel band of its edge-on face just outside one edge, as the blade
   * is rolled to the camera. In profile it is seen flat (no band); turned or head-on its shadowed
   * edge shows as a dark bevel, and from behind the other face turns up its lit edge instead. A
   * single-edged blade (`spine`: the side its back is on) always shows its thick back.
   */
  const thickness = (
    k0: number,
    k1: number,
    lo: (t: number) => number,
    hi: (t: number) => number,
    spine?: -1 | 1,
  ) => {
    if (!fine) return;
    const side = spine ?? (sk.view === 'side' ? 0 : sk.back ? lit : -lit);
    if (!side) return;
    const col = side === -lit ? mix(steel.dark, '#1c2030', 0.35) : steel.mid;
    for (let k = k0 + 0.5; k <= k1 - 0.6; k += 0.5 / res) {
      const t = (k - k0) / (k1 - k0);
      const a = lo(t);
      const b = hi(t);
      if (b - a < 0.6) continue;
      dot(at(k, (side > 0 ? b : a) + (side * 0.85) / res), col);
    }
  };
  /** A line inlaid down the middle of a blade, and inlaid stars. */
  const inlay = (k0: number, k1: number, mid: (t: number) => number = () => 0) => {
    if (f.inlay)
      for (let k = k0 + 1; k <= k0 + (k1 - k0) * 0.7; k += 0.5)
        dot(at(k, mid((k - k0) / (k1 - k0))), f.inlay);
    if (f.stars)
      for (let i = 0; i < f.stars; i++) {
        const k = k0 + 1 + ((k1 - k0 - 3) * i) / Math.max(1, f.stars - 1);
        dot(at(k, mid((k - k0) / (k1 - k0))), i % 2 ? '#f0d060' : '#ffffff');
      }
  };

  switch (w.kind) {
    case 'jian': {
      // A straight double-edged blade tapering to its point, a ridge down the middle.
      const W = f.width ?? 1;
      const taper = f.taper ?? 0.7;
      const pt = f.point ?? 0.14;
      const half = (t: number) => W * (1 - (1 - taper) * t) * (t > 1 - pt ? Math.max(0, (1 - t) / pt) : 1);
      glow(2, w.ahead, half);
      grip();
      pommel();
      strip(2, w.ahead, ...even(half), polished(steel, f.ridge));
      thickness(2, w.ahead, ...even(half));
      inlay(2, w.ahead);
      // A fuller down the shadowed side of the ridge, fading out before the point.
      if (fine)
        for (let k = 3.5; k < 2 + (w.ahead - 2) * 0.62; k += 0.25)
          dot(at(k, -0.38 * lit * half((k - 2) / (w.ahead - 2))), groove);
      guard();
      gem();
      tassel();
      return;
    }
    case 'dao': {
      // A single edge with the back bowing toward the point; the cutting edge glints.
      const W = (f.width ?? 1.3) * 1.6;
      const curve = f.curve ?? 1.5;
      const pt = f.point ?? 0.18;
      const belly = f.belly ?? 0;
      const back = (t: number) => {
        let s = -curve * t * t;
        if (f.clip && t > 0.72) s += ((t - 0.72) / 0.28) * W * 0.55;
        return s;
      };
      const front = (t: number) => {
        let wdt = W * (1 + belly * Math.pow(t, 1.5) * 0.6);
        if (t > 1 - pt) wdt *= Math.pow(Math.max(0, (1 - t) / pt), 0.75);
        return -curve * t * t + Math.max(wdt, 0.3);
      };
      glow(2, w.ahead, (t) => (front(t) - back(t)) / 2 + 0.5);
      grip();
      pommel(f.pommel ?? 'cap');
      strip(2, w.ahead, back, front, (_u, p) =>
        p > 0.8 ? steel.hi : p < 0.18 ? steel.dark : p > 0.45 ? steel.light : steel.mid,
      );
      thickness(2, w.ahead, back, front, -1);
      // A groove along the back, and the bevel line where the edge is ground.
      if (fine)
        for (let k = 3.5; k < 2 + (w.ahead - 2) * 0.8; k += 0.25) {
          const t = (k - 2) / (w.ahead - 2);
          dot(at(k, back(t) + (front(t) - back(t)) * 0.28), groove);
          dot(at(k, back(t) + (front(t) - back(t)) * 0.72), steel.light);
        }
      if (f.inlay)
        for (let k = 3; k < w.ahead * 0.75; k += 0.5)
          dot(at(k, back((k - 2) / (w.ahead - 2)) + 0.6), f.inlay);
      if (f.teeth)
        for (let k = 4; k < w.ahead - 2; k += 2) dot(at(k, back((k - 2) / (w.ahead - 2)) - 0.8), steel.dark);
      if (f.rings)
        for (let i = 0; i < f.rings; i++) {
          const k = 3.5 + ((w.ahead - 7) * i) / Math.max(1, f.rings - 1);
          const p = at(k, back((k - 2) / (w.ahead - 2)) - 1.2);
          dot(p, fit.hi);
          dot({ x: p.x, y: p.y + res }, fit.mid);
        }
      guard(f.guard ?? 'bar');
      gem();
      tassel();
      return;
    }
    case 'kris': {
      // A wavy blade: its line swings from side to side down its length, wide at the base.
      const waves = f.waves ?? 3;
      const W = f.width ?? 1;
      const mid = (t: number) => (waves ? Math.sin(t * waves * Math.PI) * 0.9 * (1 - t * 0.5) : 0);
      const half = (t: number) => W * (1.3 - 0.7 * t) * (t > 0.85 ? (1 - t) / 0.15 : 1);
      glow(1.5, w.ahead, half);
      strip(-w.behind, 0, ...even(() => 0.8), wood(c.grip));
      strip(
        1.5,
        w.ahead,
        (t) => mid(t) - half(t),
        (t) => mid(t) + half(t),
        polished(steel, f.ridge),
      );
      inlay(1.5, w.ahead, mid);
      // The asymmetric ganja at the base, flaring to one side.
      strip(
        0.3,
        1.6,
        (t) => -2.4 + t,
        () => 1.4,
        polished(fit),
      );
      return;
    }
    case 'spear':
    case 'qiang': {
      const L = (w.kind === 'qiang' ? 5 : 7) * size;
      const hb = w.ahead - L;
      shaft(-w.behind, hb, c.grip);
      strip(-w.behind - 1, -w.behind, ...even(() => 0.9), polished(fit));
      const head = w.kind === 'qiang' ? 'slim' : (f.head ?? 'leaf');
      const half =
        head === 'slim'
          ? (t: number) =>
              1.3 * size * Math.pow(Math.sin(Math.PI * Math.min(1, t * 1.25 + 0.05)), 0.6) * (1 - t * 0.2)
          : head === 'snake'
            ? (t: number) => 1.1 * size * (1 - t * 0.75)
            : (t: number) => 2.2 * size * Math.pow(Math.sin(Math.PI * Math.min(1, t * 1.1 + 0.04)), 0.75);
      const mid =
        head === 'snake'
          ? (t: number) => Math.sin(t * (f.waves ?? 3) * Math.PI) * 1.4 * size * (1 - t * 0.6)
          : () => 0;
      glow(hb, w.ahead + 1, half);
      if (head === 'trident')
        for (const s of [-1, 1]) {
          const r = 3 * size;
          strip(
            hb,
            hb + 0.9,
            () => Math.min(0, s * r),
            () => Math.max(0, s * r),
            polished(steel),
          );
          strip(
            hb,
            hb + L * 0.75,
            (t) => s * r - 0.7 + s * t * -0.6,
            (t) => s * r + 0.7 + s * t * -0.6,
            polished(steel),
          );
        }
      strip(
        hb,
        w.ahead + 1,
        (t) => mid(t) - (head === 'trident' ? half(t) * 0.6 : half(t)),
        (t) => mid(t) + (head === 'trident' ? half(t) * 0.6 : half(t)),
        polished(steel, f.ridge ?? head === 'leaf'),
      );
      if (head === 'boar') strip(hb - 0.3, hb + 0.6, ...even(() => 3.4 * size), polished(fit));
      // The socket binding head to shaft.
      strip(hb - 1.6, hb, ...even(() => 1), polished(fit));
      if (w.kind === 'qiang' || f.tassel === 'long') {
        // A full tuft of horsehair tied under the head, falling round the shaft.
        const t0 = at(hb - 1.8);
        const dark = shade(c.tassel, -0.22);
        const strands = f.tassel === 'short' ? 3 : 5;
        for (let i = -2; i <= 2; i++)
          for (let j = 0; j <= strands; j++) {
            const sway = sk.square ? 0 : flutter(sk, j / strands, j * 0.4 + i, 0.6).dx;
            dot(
              { x: t0.x + (i * 0.8 + sway + j * 0.15 * i) * res, y: t0.y + j * res },
              (i + j) % 3 ? c.tassel : dark,
            );
          }
      }
      return;
    }
    case 'staff': {
      const headed = ['rings', 'dragon', 'orb', 'skull'].includes(f.head ?? '');
      const body = headed ? c.grip : c.blade;
      shaft(-w.behind, w.ahead, body, 0.9);
      const cap = (k0: number, k1: number, half = 1.1) => strip(k0, k1, ...even(() => half), polished(fit));
      switch (f.head ?? 'caps') {
        case 'caps':
          cap(-w.behind, -w.behind + 1.6);
          cap(w.ahead - 1.6, w.ahead);
          break;
        case 'golden':
          cap(-w.behind, -w.behind + 3.2, 1.4);
          cap(w.ahead - 3.2, w.ahead, 1.4);
          glow(-w.behind, w.ahead, () => 1);
          break;
        case 'rings': {
          // A monk's staff: a loop of bronze at the top, rings threaded on it.
          cap(-w.behind, -w.behind + 1.2);
          const top = at(w.ahead + 2.6);
          for (let a = 0; a < Math.PI * 2; a += 0.3)
            dot(
              { x: top.x + Math.cos(a) * 2.8 * res, y: top.y + Math.sin(a) * 2.8 * res },
              Math.cos(a) * 0.6 - Math.sin(a) * 0.8 > 0 ? steel.hi : steel.mid,
            );
          for (let i = 0; i < (f.rings ?? 6); i++) {
            const a = Math.PI * (0.25 + (i / Math.max(1, (f.rings ?? 6) - 1)) * 1.5);
            ball(
              g,
              { x: top.x + Math.cos(a) * 3.6 * res, y: top.y + (Math.sin(a) * 3.6 + 1) * res },
              0.9 * res,
              steel,
            );
          }
          break;
        }
        case 'dragon':
          cap(-w.behind, -w.behind + 1.4);
          ball(g, at(w.ahead + 1.5), 2.2 * res, steel);
          dot(at(w.ahead + 2.5, 2 * lit), c.tassel);
          line(g, at(w.ahead + 2, -2), at(w.ahead + 4, -3), steel.light);
          break;
        case 'skull':
          ball(g, at(w.ahead + 1.8), 2.2 * res, steel);
          dot(at(w.ahead + 1.8, 0.8), steel.dark);
          dot(at(w.ahead + 1.8, -0.8), steel.dark);
          break;
        case 'orb':
          cap(-w.behind, -w.behind + 1.2);
          cap(w.ahead - 1, w.ahead, 1.6);
          ball(g, at(w.ahead + 2.2), 2 * res, steel);
          glow(w.ahead, w.ahead + 3, () => 2);
          break;
      }
      return;
    }
    case 'umbrella': {
      // Furled: a shaft, the folded canopy bunched toward the tip, a tassel at the handle.
      shaft(-w.behind, w.ahead + 2, c.grip, 0.6);
      const canopy = metal(c.blade, c.edge);
      strip(5, w.ahead - 1, ...even((t) => 1.6 * Math.sin(t * Math.PI) + 0.6), (u, _p, _t, k) =>
        Math.floor(k) % 3 === 0 ? canopy.dark : u > 0.4 ? canopy.hi : u < -0.3 ? canopy.mid : canopy.light,
      );
      if (f.head === 'blade') {
        glow(w.ahead, w.ahead + 6, () => 0.6);
        strip(w.ahead + 2, w.ahead + 6, ...even((t) => 0.9 * (1 - t)), polished(metal('#e6ecf4', '#7f93b0')));
      }
      tassel(3, at(-w.behind));
      return;
    }
    case 'fan': {
      const paper = metal(c.blade, c.edge);
      const rib = f.head === 'iron' ? polished(fit) : wood(c.grip);
      if (open) {
        // Spread: ribs fanning from the pivot in the hand, the leaf across their outer half.
        const span = f.head === 'feather' ? 0.75 : 0.85;
        for (let a = -span, i = 0; a <= span + 1e-6; a += 0.08 / res, i++) {
          const r = { x: Math.cos(angle + a), y: Math.sin(angle + a) };
          const lightSide = r.x * 0.6 - r.y * 0.8;
          for (let k = 1.5; k <= w.ahead + (f.head === 'feather' ? (i % 2) * 1.5 : 0); k += 0.5 / res) {
            const leaf = k > w.ahead * 0.45;
            const col = leaf
              ? i % 4 === 0
                ? paper.mid
                : lightSide > 0.2
                  ? paper.hi
                  : paper.light
              : i % 2
                ? c.grip
                : shade(c.grip, 0.12);
            dot({ x: hand.x + r.x * k * res, y: hand.y + r.y * k * res }, col);
          }
          if (f.head === 'iron' && i % 2 === 0)
            dot({ x: hand.x + r.x * (w.ahead + 1) * res, y: hand.y + r.y * (w.ahead + 1) * res }, fit.hi);
        }
        glow(w.ahead - 2, w.ahead + 1, () => 3);
        return;
      }
      strip(-w.behind, w.ahead, ...even((t) => 0.8 + t * 0.5), rib);
      dot(hand, c.tassel);
      tassel(2, { x: hand.x, y: hand.y + 1 });
      return;
    }
    case 'spike': {
      // หนาม (emei piercers): a steel rod pointed at both ends, a ring round the middle finger.
      const half = (t: number) => 0.9 * Math.sin(Math.PI * t);
      glow(-w.behind, w.ahead, half);
      strip(-w.behind, w.ahead, ...even(half), polished(steel, true));
      const r = at(0, -1.6);
      dot(r, fit.hi);
      dot({ x: r.x + res, y: r.y }, fit.mid);
      return;
    }
    case 'chain': {
      // Links trailing from the hand, drooping under their weight and swaying, a head at the end.
      let prev: Pt = hand;
      const seg = f.head === 'sections' ? 3 : 1;
      for (let k = 1; k <= w.ahead; k++) {
        const t = k / w.ahead;
        const sway = sk.square ? { dx: 0, dy: 0 } : flutter(sk, t, k * 0.3, 0.6);
        const p = {
          x: hand.x + (d.x * k * 0.7 + sway.dx) * res,
          y: hand.y + (d.y * k * 0.7 + t * t * 9 + sway.dy) * res,
        };
        line(g, prev, p, Math.floor(k / seg) % 2 ? steel.light : steel.mid);
        if (seg > 1 && k % seg === 0) dot(p, steel.hi);
        prev = p;
      }
      const e = { x: prev.x + d.x * 2 * res, y: prev.y + (d.y * 2 + 1) * res };
      if (f.head === 'ball') {
        ball(g, e, 2 * size * res, steel);
        for (const [dx, dy] of [
          [0, -1],
          [1, 0],
          [0, 1],
          [-1, 0],
        ] as const)
          dot({ x: e.x + dx * (2 * size + 1) * res, y: e.y + dy * (2 * size + 1) * res }, steel.dark);
      } else if (f.head === 'sickle') {
        for (let a = 0; a <= 2.2; a += 0.25)
          dot(
            { x: e.x + Math.cos(a - 1.2) * 3 * res, y: e.y + Math.sin(a - 1.2) * 3 * res },
            a < 1.1 ? steel.hi : steel.light,
          );
      } else segment(g, prev, e, 2, 1, steel.light, steel.dark);
      if (f.glow) {
        dot({ x: e.x + 3 * res, y: e.y - 2 * res }, f.glow);
        dot({ x: e.x - 2 * res, y: e.y + 3 * res }, f.glow);
      }
      return;
    }
    case 'bow': {
      // A bow held upright across the aim, bellied forward, the tips curled on a recurve, its string behind.
      const span = Math.round(11 * size);
      const limb = metal(c.blade, shade(c.blade, -0.2));
      let prev: Pt | null = null;
      for (let s = -span; s <= span; s++) {
        const u = s / span;
        const curl = f.head === 'recurve' && Math.abs(u) > 0.8 ? (Math.abs(u) - 0.8) * 9 : 0;
        const belly = 3 * (1 - u * u) - (Math.abs(u) > 0.85 ? 1 : 0) + curl;
        const p = at(belly, s);
        if (prev) {
          line(g, prev, p, Math.abs(s) < 2 ? c.guard : s * lit < 0 ? limb.light : limb.mid);
          dot(at(belly + 0.8, s), Math.abs(s) < 2 ? shade(c.guard, -0.2) : limb.dark);
        }
        prev = p;
      }
      line(g, at(-0.5, -span), at(-0.5, span), c.edge);
      if (f.glow) for (let s = -span; s <= span; s += 4) dot(at(4, s), f.glow);
      return;
    }
    case 'glaive': {
      // ง้าว: a long shaft and a broad crescent blade, a spur at its back and a ring of tassel below it.
      const L = 8 * size;
      const hb = w.ahead - L + 1;
      shaft(-w.behind, hb, c.grip);
      strip(-w.behind - 1, -w.behind, ...even(() => 0.9), polished(fit));
      const depth = (t: number) =>
        (1.2 + 2.4 * size * Math.sin(Math.PI * Math.min(1, t * 0.95)) + t * 1.4 * size) *
        (t > 0.86 ? (1 - t) / 0.14 + 0.15 : 1);
      glow(hb, w.ahead + 1, depth);
      strip(
        hb,
        w.ahead + 1,
        (t) => -depth(t),
        () => (f.head === 'beak' ? 1.2 : 0.9),
        (_u, p) => (p < 0.15 ? steel.hi : p < 0.4 ? steel.light : p > 0.88 ? steel.dark : steel.mid),
      );
      if (f.teeth) for (let k = hb + 1; k < w.ahead - 1; k += 2) dot(at(k, 1.6), steel.dark);
      // The back spur (a hooked beak on a phoenix glaive).
      if (f.head === 'beak') {
        line(g, at(hb + 2, 1.2), at(hb + 4, 3.2), steel.light);
        line(g, at(hb + 4, 3.2), at(hb + 6, 2.4), steel.mid);
      } else {
        dot(at(hb + 1.5, 1.8), steel.light);
        dot(at(hb + 2.3, 2.4), steel.mid);
      }
      if (f.guard === 'beast') strip(hb - 2, hb + 0.6, ...even((t) => 2.2 - t), polished(fit));
      else strip(hb - 1.2, hb, ...even(() => 1.2), polished(fit));
      const t0 = at(hb - 2.2);
      for (let i = -1; i <= 1; i++)
        for (let j = 0; j <= 3; j++)
          dot({ x: t0.x + i * res, y: t0.y + j * res }, (i + j) % 2 ? c.tassel : shade(c.tassel, -0.2));
      return;
    }
    case 'halberd': {
      // 戟: a spear point with a crescent blade on one side of it, or both on a sky-piercing ji.
      const hb = w.ahead - 6 * size;
      shaft(-w.behind, hb, c.grip);
      strip(-w.behind - 1, -w.behind, ...even(() => 0.9), polished(fit));
      const half = (t: number) => 1.3 * size * Math.pow(Math.sin(Math.PI * Math.min(1, t + 0.05)), 0.7);
      glow(hb, w.ahead + 3, () => 3.5 * size);
      strip(hb, w.ahead + 3, ...even(half), polished(steel, true));
      const sides = f.head === 'double' ? [-1, 1] : [-1];
      for (const s of sides) {
        if (f.head === 'axe')
          strip(
            hb,
            hb + 3.2 * size,
            () => -3.6 * size,
            () => -0.8,
            (_u, p) => (p < 0.2 ? steel.hi : p > 0.8 ? steel.dark : steel.light),
          );
        else
          strip(
            hb - 0.5,
            hb + 4 * size,
            (t) => (s < 0 ? -(1.4 + Math.sin(Math.PI * t) * 2.6 * size) : 0.8),
            (t) => (s < 0 ? -0.8 : 1.4 + Math.sin(Math.PI * t) * 2.6 * size),
            (_u, p) => {
              const out = s < 0 ? 1 - p : p;
              return out > 0.82 ? steel.hi : out < 0.25 ? steel.dark : steel.light;
            },
          );
        if (f.teeth)
          for (let k = hb; k < hb + 4 * size; k += 1.5) dot(at(k, s * (2 + 2.6 * size)), steel.dark);
      }
      strip(hb - 1.6, hb, ...even(() => 1.1), polished(fit));
      if (f.tassel === 'long') {
        const t0 = at(hb - 2);
        for (let j = 0; j <= 4; j++) {
          dot({ x: t0.x, y: t0.y + j * res }, c.tassel);
          dot({ x: t0.x + res, y: t0.y + j * res }, shade(c.tassel, -0.2));
        }
      }
      return;
    }
    case 'axe': {
      shaft(-w.behind, w.ahead + 0.5, c.grip, 0.7);
      const a0 = w.ahead - 4.5 * size;
      const head = f.head ?? 'bearded';
      const reach = (t: number) =>
        head === 'broad'
          ? 3.2 * size
          : head === 'bearded'
            ? (1.6 + 2.6 * Math.pow(t, 0.8)) * size
            : (1.4 + 2.8 * Math.sin(Math.PI * t)) * size;
      glow(a0, w.ahead, reach);
      for (const s of head === 'double' ? [-1, 1] : [-1]) {
        strip(
          a0,
          w.ahead,
          (t) => (s < 0 ? -reach(t) : 0.6),
          (t) => (s < 0 ? -0.6 : reach(t)),
          (_u, p) => {
            const out = s < 0 ? 1 - p : p;
            return out > 0.82 ? steel.hi : out > 0.45 ? steel.light : out < 0.15 ? steel.dark : steel.mid;
          },
        );
        if (f.teeth)
          for (let k = a0 + 0.5; k < w.ahead; k += 1.5)
            dot(at(k, s * (reach((k - a0) / (w.ahead - a0)) + 0.8)), steel.dark);
      }
      // The poll behind the blade.
      if (head !== 'double')
        strip(
          a0 + 1,
          a0 + 3,
          () => 0.6,
          () => 1.8,
          polished(steel),
        );
      return;
    }
    case 'hammer': {
      shaft(-w.behind, w.ahead - 2 * size, c.grip, 0.6);
      const h = at(w.ahead);
      const r = 2.5 * size * res;
      const head = f.head ?? 'round';
      if (f.glow)
        for (let a = 0; a < 6.28; a += 1.05)
          dot({ x: h.x + Math.cos(a) * (r + 2 * res), y: h.y + Math.sin(a) * (r + 2 * res) }, f.glow);
      if (head === 'spiked')
        for (let a = 0; a < 6.28; a += 0.785)
          line(
            g,
            h,
            { x: h.x + Math.cos(a) * (r + 1.6 * res), y: h.y + Math.sin(a) * (r + 1.6 * res) },
            steel.dark,
          );
      if (head === 'skull') {
        ball(g, h, r, steel);
        px(g, h.x - 1.5 * res, h.y - res / 2, res, res, '#1a1418');
        px(g, h.x + 0.5 * res, h.y - res / 2, res, res, '#1a1418');
        px(g, h.x - res, h.y + 1.5 * res, 2 * res, res, steel.dark);
      } else
        ball(
          g,
          h,
          r,
          steel,
          head === 'octa' ? 'octa' : head === 'square' ? 'square' : head === 'melon' ? 'melon' : 'round',
        );
      dot(h, fit.hi);
      return;
    }
    case 'whisk': {
      // 拂塵: a short handle, a long hank of horsehair falling from its end.
      strip(-w.behind, w.ahead, ...even(() => 0.6), wood(c.grip));
      const tip = at(w.ahead);
      const hair = metal(c.blade, c.edge);
      const strands = size > 1 ? [-1.5, -0.5, 0.5, 1.5] : [-1, 0, 1];
      const len = Math.round(11 * size);
      for (const off of strands) {
        let prev: Pt = { x: tip.x + off * res, y: tip.y };
        for (let i = 1; i <= len; i++) {
          const sway = sk.square ? { dx: 0, dy: 0 } : flutter(sk, i / len, i * 0.4, 0.8);
          const p = { x: tip.x + (off + d.x * i * 0.25 + sway.dx) * res, y: tip.y + (i + sway.dy) * res };
          line(g, prev, p, off > 0 ? hair.hi : (i + off) % 4 ? hair.light : hair.dark);
          prev = p;
        }
        if (f.glow && off === strands[0]) dot({ x: prev.x - 1.5 * res, y: prev.y - 2 * res }, f.glow);
      }
      return;
    }
    case 'hook': {
      // 鉤: a straight blade ending in a hook bent back, a crescent guard over the hand.
      const W = f.width ?? 1;
      const half = () => 0.9 * W;
      glow(2, w.ahead, half);
      grip();
      strip(2, w.ahead, ...even(half), polished(steel, true));
      const hk = 2.5 * size;
      for (let a = 0; a <= Math.PI; a += 0.2) {
        const p = at(w.ahead + Math.sin(a) * hk, -hk + Math.cos(a) * hk);
        dot(p, a < 1.2 ? steel.hi : steel.light);
        dot(at(w.ahead + Math.sin(a) * (hk - 0.8), -hk + Math.cos(a) * (hk - 0.8)), steel.mid);
      }
      if (f.teeth) for (let k = 4; k < w.ahead; k += 2) dot(at(k, 1.6), steel.dark);
      for (const s of [-1, 1]) {
        line(g, at(1.2, s * 3), at(-1.2, s * 2.2), s === lit ? fit.hi : fit.mid);
        line(g, at(1.2, s * 3), at(2.6, s * 4), fit.light);
      }
      pommel('cap');
      return;
    }
    case 'whip': {
      if (f.head === 'rod') {
        // 鞭: a segmented iron rod, knuckled every two pixels.
        glow(1, w.ahead, () => 1);
        grip();
        strip(1, w.ahead, ...even((t) => 1.1 - t * 0.4), (u, _p, _t, k) =>
          Math.floor(k) % 2 === 0 ? (u > 0 ? steel.hi : steel.mid) : u > 0.3 ? steel.light : steel.dark,
        );
        guard('bar');
        pommel('knob');
        return;
      }
      // A plaited whip: heavier near the hand, tapering, sagging under its weight.
      strip(-w.behind, 1, ...even(() => 0.7), wrapped(c.grip));
      let prev: Pt = at(1);
      const lash = metal(c.blade, c.edge);
      for (let k = 2; k <= w.ahead; k++) {
        const t = k / w.ahead;
        const sway = sk.square ? { dx: 0, dy: 0 } : flutter(sk, t, k * 0.3, 0.8);
        const p = {
          x: hand.x + (d.x * k * 0.8 + sway.dx) * res,
          y: hand.y + (d.y * k * 0.8 + t * t * 12 + sway.dy) * res,
        };
        line(g, prev, p, f.head === 'bone' ? (k % 2 ? lash.hi : lash.dark) : k % 3 ? lash.light : lash.dark);
        if (f.teeth && k % 3 === 0) dot({ x: p.x, y: p.y - res }, lash.dark);
        if (f.glow && k % 5 === 0) dot({ x: p.x + res, y: p.y - 2 * res }, f.glow);
        prev = p;
      }
      return;
    }
    case 'flute': {
      // A flute: a round tube lit along its length, finger holes, a tassel from the end.
      glow(-w.behind, w.ahead, () => 0.8);
      const tube = metal(c.blade, c.edge);
      strip(-w.behind, w.ahead, ...even(() => 0.8), (u) =>
        u > 0.4 ? tube.hi : u < -0.4 ? tube.dark : tube.light,
      );
      for (let k = 3; k < w.ahead; k += 2) dot(at(k, 0.3 * lit), '#1a1418');
      strip(w.ahead - 0.6, w.ahead, ...even(() => 0.9), polished(fit));
      tassel(4, at(-w.behind));
      return;
    }
    case 'brush': {
      // A great calligraphy brush: a long shaft, a ferrule, the hair swelling then pointed.
      const L = 5 * size;
      strip(-w.behind, w.ahead - L, ...even(() => 0.7), wood(c.grip));
      strip(w.ahead - L - 1.2, w.ahead - L, ...even(() => 1.1), polished(fit));
      const hair = metal(c.edge, c.blade);
      glow(w.ahead - L, w.ahead + 1, () => 1.6 * size);
      strip(
        w.ahead - L,
        w.ahead + 1,
        ...even((t) => 1.6 * size * Math.sin(Math.PI * Math.min(1, 0.35 + t * 0.7))),
        (u) => (u > 0.5 ? hair.light : u < -0.3 ? hair.dark : hair.mid),
      );
      return;
    }
    case 'fist':
      return;
  }
}

/** Cloth wraps round the wrist and knuckles, or knuckle plates, claws or a gauntlet (หมัด). */
function fistWrap(g: G, sk: Skeleton, side: Side, w: WeaponDesign): void {
  const wr = sk.joints[`wrist${side}`];
  const hand = sk.joints[`hand${side}`];
  const c = w.colours;
  const f = w.form ?? {};
  const dx = hand.x - wr.x;
  const dy = hand.y - wr.y;
  const l = Math.hypot(dx, dy) || 1;
  const back = { x: wr.x - dx * 0.8, y: wr.y - dy * 0.8 };
  if (f.head === 'gauntlet')
    segment(g, { x: wr.x - dx * 1.6, y: wr.y - dy * 1.6 }, hand, 5, 4, c.blade, c.edge);
  else segment(g, back, wr, 4, 4, c.blade, c.edge);
  const plate = metal(c.guard, shade(c.guard, -0.3));
  if (f.head === 'knuckle' || f.head === 'gauntlet') {
    px(g, hand.x - 1, hand.y - 1, 3, 2, plate.light);
    px(g, hand.x, hand.y - 1, 1, 1, plate.hi);
    px(g, hand.x - 1, hand.y + 1, 3, 1, plate.dark);
  } else px(g, hand.x - 1, hand.y - 1, 2, 2, c.guard);
  if (f.head === 'claw') {
    // Three blades curving out past the knuckles.
    const s = f.size ?? 1;
    const ux = dx / l;
    const uy = dy / l;
    for (const o of [-1.2, 0, 1.2])
      for (let k = 1; k <= 4 * s; k++) {
        const bend = (k / (4 * s)) ** 2 * 1.5;
        px(
          g,
          Math.round(hand.x + ux * k - uy * (o + bend)),
          Math.round(hand.y + uy * k + ux * (o + bend)),
          1,
          1,
          k < 2 ? plate.mid : plate.hi,
        );
      }
  }
  if (f.glow) {
    px(g, hand.x + 2, hand.y - 2, 1, 1, f.glow);
    px(g, hand.x - 2, hand.y + 1, 1, 1, f.glow);
  }
}

/**
 * Where a held weapon lies in a frame, in art pixels of the composed frame (PAD_TOP included):
 * the hand, the way the weapon points, how far it reaches ahead, and whether it is behind the body.
 * Forging effects (render/forgeFx.ts) run along it.
 */
export interface BladeLine {
  hx: number;
  hy: number;
  dx: number;
  dy: number;
  len: number;
  behind: boolean;
}

/** The lines of the weapon(s) held in a frame (none for fist wraps). */
export function bladeLines(w: WeaponDesign, sk: Skeleton): BladeLine[] {
  if (w.hands === 'fists') return [];
  const sides: Side[] = w.hands === 'right' ? ['A'] : w.hands === 'left' ? ['B'] : ['A', 'B'];
  return sides.map((side) => {
    const hand = sk.joints[`hand${side}`];
    const { angle, behind, fore } = weaponAim(sk, side);
    return {
      hx: hand.x,
      hy: hand.y + PAD_TOP,
      dx: Math.cos(angle),
      dy: Math.sin(angle),
      len: w.ahead * fore,
      behind,
    };
  });
}

/**
 * How much of "ahead" (the way the figure faces) runs toward the camera in each view: all of it
 * head-on, none in profile, away from it seen from behind.
 */
const FACING_DEPTH: Record<View, number> = { front: 1, front3: 0.55, side: 0, back3: -0.55, back: -1 };
/** Depth gained per art pixel of a weapon pointing straight at the camera (a body is about 8 deep). */
const DEPTH_PER_PX = 0.12;

/**
 * Depth along a weapon or scabbard lying from `from` (art pixels) along `angle` on screen, `z0` there
 * and changing by `perPx` each art pixel of its real length (`fore`: how much of it shows on screen).
 */
function alongDepth(
  from: Pt,
  angle: number,
  fore: number,
  res: number,
  z0: number,
  perPx: number,
  max = 1.2,
): (x: number, y: number) => number {
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  const ox = from.x * res;
  const oy = from.y * res;
  const f = res * Math.max(0.35, fore);
  return (x, y) => Math.max(-0.95, Math.min(max, z0 + (((x - ox) * dx + (y - oy) * dy) / f) * perPx));
}

/** Poses that open a fan: spells and open-handed flourishes. */
const OPEN_FAN = new Set<Rig['arm']>(['raise', 'out', 'chest', 'guard']);

/** The weapon(s) in hand; behind the body when the pose holds them there. */
export function weaponLayer(
  weapon: string | WeaponDesign,
  /**
   * How it is carried when put away (left out: always in hand, as in the studio): whether it is
   * sheathed now, and the scabbard's colour for a sword or sabre worn at the hip.
   */
  carry?: { sheathed?: boolean; scabbard?: string },
): Layer {
  const w = typeof weapon === 'string' ? WEAPON_BY_ID[weapon] : weapon;
  return {
    id: 'weapon',
    label: 'อาวุธ',
    pieces(sk, fit) {
      if (!w) return [];
      const res = fit.res ?? 1;
      // A one-handed weapon held at ease in the off hand (motions/hold.ts `revArm`) is drawn in that hand.
      const sides: Side[] =
        w.hands === 'right'
          ? [sk.rig.far === 'revArm' ? 'B' : 'A']
          : w.hands === 'left'
            ? ['B']
            : ['A', 'B'];
      const step = carry ? stowStep(sk, !!carry.sheathed) : ({ mode: 'hand', t: 0 } as const);
      return sides.flatMap((side): Piece[] => {
        const hand = sk.joints[`hand${side}`];
        if (w.hands === 'fists')
          return [
            {
              z: hand.z + 0.01,
              draw: (g) => {
                g.save();
                g.scale(res, res);
                fistWrap(g, sk, side, w);
                g.restore();
              },
            },
          ];
        const stow = carry ? stowAt(sk, w, side) : undefined;
        const out: Piece[] = [];
        // A sword or sabre's scabbard is worn at the hip whether the blade is in it or in hand.
        // Head-on, the scabbard runs from its mouth at the belt back and down past the hip: from the
        // front its mouth lies over the robe and its tip goes behind the leg; from behind the other
        // way round. Turned, it hangs wholly on the near or the far side.
        const scabbardDepth =
          stow?.scabbard && sk.square
            ? alongDepth(
                stow.at,
                stow.angle,
                1,
                res,
                sk.back ? -0.1 : zOf(sk, 'chest', 0.07),
                -0.89 * FACING_DEPTH[sk.view] * DEPTH_PER_PX,
                // Never out past the hand hanging beside it.
                0.45,
              )
            : undefined;
        const scabbardZ = scabbardDepth
          ? sk.back
            ? -0.1
            : zOf(sk, 'chest', 0.07)
          : stow?.behind
            ? -0.9
            : 0.9;
        if (stow?.scabbard && step.mode === 'hand')
          out.push({
            z: scabbardZ,
            depth: scabbardDepth,
            draw: (g) => drawScabbard(g, w, stow, res, carry?.scabbard),
          });
        if (stow && step.mode !== 'hand') {
          // Put away, or sliding home: along its carry line, drawn out by `k` of its length.
          const k = step.mode === 'slide' ? step.k : 0;
          const d = { x: Math.cos(stow.angle), y: Math.sin(stow.angle) };
          const at = { x: stow.at.x - d.x * w.ahead * k, y: stow.at.y - d.y * w.ahead * k };
          if (!stow.scabbard) {
            out.push({
              z: stow.behind ? -0.9 : 0.9,
              draw: (g) => drawWeapon(g, w, { x: at.x * res, y: at.y * res }, stow.angle, sk, false, res),
            });
            return out;
          }
          // In its scabbard: the scabbard where it hangs, and the hilt (all that shows of the blade,
          // and as much as is drawn out) on its own, in front of the body.
          const shown = { ...w, ahead: 1 + w.ahead * k };
          out.push(
            {
              z: scabbardZ,
              depth: scabbardDepth,
              draw: (g) => drawScabbard(g, w, stow, res, carry?.scabbard),
            },
            {
              z: stow.hiltBehind ? -0.9 : 0.9,
              draw: (g) => drawWeapon(g, shown, { x: at.x * res, y: at.y * res }, stow.angle, sk, false, res),
            },
          );
          return out;
        }
        // Being put away or drawn: the blade turns from the guard straight toward its carry line as
        // the hand brings it there (not through the angles the arm's other positions hold a blade at).
        const moving = stowPhase(sk.pose, sk.phase) !== null;
        const aim = weaponAim(
          moving ? { ...sk, rig: { ...sk.rig, arm: 'guard', armTo: undefined } } : sk,
          side,
        );
        const angle =
          stow && step.mode === 'hand' && step.t ? turnToward(aim.angle, stow.angle, step.t) : aim.angle;
        const open = w.kind === 'fan' && OPEN_FAN.has(sk.rig.arm);
        // Under the hand, so the gripping fingers close over the grip; facing the camera, over the
        // sleeve of its own arm (the hand holds it out in front of the arm), facing away under it.
        // Its depth runs along the blade as it points toward the camera or away, so a blade held
        // ahead goes behind a figure seen from behind and a strike passes in front of the body and
        // behind it as it turns.
        // Held behind the back (motions/hold.ts `reverseUp`): seen from behind it lies over the back, as a sword slung
        // there does; from the front behind the body (lane C0-POSE-FIX).
        const overBack = sk.back && (sk.rig.armTo ?? sk.rig.arm) === 'reverseUp';
        // Reversed along the off forearm (`revArm`): head-on it lies over the sleeve, so the blade shows beside the arm.
        const overArm = side === 'B' && sk.rig.far === 'revArm' && sk.square;
        const z0 = overBack ? 0.9 : overArm ? hand.z + 0.3 : aim.behind ? -0.8 : hand.z + (sk.back ? -0.005 : 0.06);
        out.push({
          z: z0,
          depth: aim.behind || overBack
            ? undefined
            : alongDepth(hand, angle, aim.fore, res, z0, aim.ahead * FACING_DEPTH[sk.view] * DEPTH_PER_PX),
          draw: (g) => drawWeapon(g, w, { x: hand.x * res, y: hand.y * res }, angle, sk, open, res, aim.fore),
        });
        return out;
      });
    },
  };
}

/** Where a weapon goes when it is put away. */
export type Carry = 'hip' | 'back' | 'belt' | 'none';

/** Swords, sabres and krises go in a scabbard at the hip; long arms across the back; the rest in the belt. */
export function carryOf(w: WeaponDesign): Carry {
  if (w.hands === 'fists') return 'none';
  if (w.kind === 'jian' || w.kind === 'dao' || w.kind === 'kris') return 'hip';
  if (['spear', 'qiang', 'staff', 'glaive', 'halberd', 'bow', 'umbrella'].includes(w.kind)) return 'back';
  return w.ahead + w.behind > 20 ? 'back' : 'belt';
}

/** Where (art pixels, the grip) and at what angle a weapon held in hand `side` is carried. */
export function stowAt(
  sk: Skeleton,
  w: WeaponDesign,
  side: Side = 'A',
): { at: Pt; angle: number; behind: boolean; hiltBehind: boolean; scabbard: boolean } {
  const j = sk.joints;
  const carry = carryOf(w);
  // Which way the figure faces across the screen (turned and in profile it faces right).
  const fwd = sk.square ? 0 : 1;
  // A second weapon of a pair goes on the other side.
  const mirror = side === 'B' ? -1 : 1;
  if (carry === 'back') {
    // Across the back, head up past the shoulder; over it seen from behind.
    // Slung on the back: turned or in profile it lies along the back, nearly upright, its head
    // rising past the shoulder; head-on (and from behind) slanting across it, the ends standing out
    // past the shoulder and the hip. Its middle is at the middle of the back.
    const sx = sk.square ? (sk.view === 'front' ? 1 : -1) * mirror : -fwd * mirror;
    // Turned toward the camera it crosses the back the other way, its head showing over the near
    // shoulder (as it would from the front three-quarter) and its foot past the far hip.
    const front3 = sk.view === 'front3';
    // The slant across the back belongs to the body (head over one shoulder, foot past the other hip): a left
    // view (rig/dir8.ts), drawn with the body's sides exchanged and mirrored once composed, crosses it the other
    // way so that, mirrored, it lies as the body wears it (lane C0-WEAPON, open item of C0-8DIR).
    const across = sk.left ? -mirror : mirror;
    const angle = sk.square
      ? Math.atan2(-0.85, 0.53 * sx)
      : front3
        ? Math.atan2(-0.85, 0.53 * across)
        : Math.atan2(-0.96, 0.28 * (sk.left && sk.back ? -sx : sx));
    const mid = (w.ahead - w.behind) / 2;
    // Against the back (outside its outline, where it shows), its middle a little above the waist.
    const at = {
      x: j.chest.x - fwd * (front3 ? 2 : 7) - Math.cos(angle) * mid,
      y: j.chest.y + 7 - Math.sin(angle) * mid,
    };
    return { at, angle, behind: !sk.back, hiltBehind: !sk.back, scabbard: false };
  }
  // At the hip: a blade at the left (a cross-draw), anything else in the belt on the right.
  const hipSide: Side = (carry === 'hip') === mirror > 0 ? 'B' : 'A';
  const hip = j[`hip${hipSide}`];
  const out = Math.sign(hip.x - j.pelvis.x) || 1;
  if (carry === 'hip') {
    // Hilt forward and up at the belt, across the front of the body, the scabbard trailing back and
    // down past the hip (behind it when that hip is the far one).
    const at = { x: hip.x + fwd * 4.5, y: j.pelvis.y - 3 };
    // Turned, about 38° below level so it hangs along the robe, not out behind like a pole (ns-art POSES-R3 fix 2).
    const angle = sk.square ? Math.atan2(0.9, 0.45 * out) : Math.atan2(0.7, -fwd * 0.9);
    return { at, angle, behind: hip.z < 0, hiltBehind: sk.back, scabbard: true };
  }
  const at = { x: hip.x + fwd, y: j.pelvis.y - 2 };
  const behind = hip.z < 0;
  return {
    at,
    angle: Math.atan2(1, sk.square ? 0.25 * out : 0.3 * fwd),
    behind,
    hiltBehind: behind,
    scabbard: false,
  };
}

/**
 * The step of putting a weapon away or drawing it this frame: in hand (turned `t` of the way to its
 * carry line), sliding home (`k` of it still out), or put away.
 */
export function stowStep(
  sk: Skeleton,
  sheathed: boolean,
): { mode: 'hand'; t: number } | { mode: 'slide'; k: number } | { mode: 'stow' } {
  // Standing or on the move (walkSheathe / walkUnsheathe).
  const step = stowPhase(sk.pose, sk.phase);
  if (step !== null) return stowStepAt(step);
  return sheathed ? { mode: 'stow' } : { mode: 'hand', t: 0 };
}

/**
 * The weapon at step `ph` of putting it away (drawing it runs the steps backwards), in time with the
 * arm (`sheatheArm` in sprites.ts): turning in the hand toward its carry line while the hand brings
 * it down to the hip, sliding home (`k` of it still out) while the hand holds there, then put away.
 */
function stowStepAt(
  ph: number,
): { mode: 'hand'; t: number } | { mode: 'slide'; k: number } | { mode: 'stow' } {
  const u = ph / (SHEATHE_FRAMES - 1);
  if (u < 0.5) return { mode: 'hand', t: Math.min(0.92, (u / 0.5) * 0.92) };
  if (u < 0.78) return { mode: 'slide', k: 0.6 * (1 - (u - 0.5) / 0.28) };
  return { mode: 'stow' };
}

/** The angle `t` of the way from `a` to `b`, the short way round. */
function turnToward(a: number, b: number, t: number): number {
  const d = Math.atan2(Math.sin(b - a), Math.cos(b - a));
  return a + d * t;
}

/**
 * A scabbard along a blade's carry line, from just past the guard to beyond the point: leather (or
 * lacquer) in the look's colour, rounded to the light along one edge, with metal at the mouth and
 * the chape at the tip — pixel by pixel, so it stays crisp at any angle.
 */
function drawScabbard(
  g: G,
  w: WeaponDesign,
  stow: { at: Pt; angle: number },
  res: number,
  colour = '#3a2a22',
): void {
  const d = { x: Math.cos(stow.angle), y: Math.sin(stow.angle) };
  const n = { x: -d.y, y: d.x };
  const len = w.ahead + 1;
  const start = 0.8;
  const f = w.form ?? {};
  // Wider than the blade it holds; a sabre's widens toward the tip.
  const half = (t: number) =>
    ((f.width ?? (w.kind === 'dao' ? 1.4 : 1)) + 0.7 + (w.kind === 'dao' ? 0.5 * t : 0)) * res;
  const lit = n.x * 0.6 - n.y * 0.8 >= 0 ? 1 : -1;
  const base = colour;
  const light = shade(colour, 0.12);
  const dark = shade(colour, -0.12);
  const gloss = shade(colour, 0.3);
  const deep = shade(colour, -0.28);
  const fitting = w.colours.guard;
  const fitDark = shade(w.colours.guard, -0.25);
  const ax = stow.at.x * res;
  const ay = stow.at.y * res;
  const ends = [start, len].map((k) => ({ x: ax + d.x * k * res, y: ay + d.y * k * res }));
  const pad = 4 * res;
  const x0 = Math.floor(Math.min(ends[0]!.x, ends[1]!.x) - pad);
  const x1 = Math.ceil(Math.max(ends[0]!.x, ends[1]!.x) + pad);
  const y0 = Math.floor(Math.min(ends[0]!.y, ends[1]!.y) - pad);
  const y1 = Math.ceil(Math.max(ends[0]!.y, ends[1]!.y) + pad);
  for (let Y = y0; Y <= y1; Y++)
    for (let X = x0; X <= x1; X++) {
      const vx = X + 0.5 - ax;
      const vy = Y + 0.5 - ay;
      const along = (vx * d.x + vy * d.y) / res;
      if (along < start || along > len) continue;
      const t = (along - start) / (len - start);
      const across = vx * n.x + vy * n.y;
      // The point end rounds off.
      const h = half(t) * (t > 0.9 ? Math.sqrt(Math.max(0, 1 - ((t - 0.9) / 0.1) ** 2)) : 1);
      if (Math.abs(across) > h) continue;
      const u = (across / h) * lit;
      const metal = along < start + 1.2 || t > 0.86;
      // A round case: the lit side, a one-pixel gloss line just in from it, the body, the shadow
      // side and its darkest edge where it turns away (lacquer and leather both shine).
      const edge = 1 - 0.7 / Math.max(1, h);
      g.fillStyle = metal
        ? u > 0.5
          ? shade(w.colours.guard, 0.18)
          : u > -0.2
            ? fitting
            : fitDark
        : u > edge
          ? light
          : u > edge - 0.38
            ? gloss
            : u > 0.2
              ? light
              : u < -edge
                ? deep
                : u < -0.4
                  ? dark
                  : base;
      g.fillRect(X, Y, 1, 1);
    }
}
